// The properties panel (slices 06 to 09; design/07 §4, design/08 §3,
// design/12, design/10, design/11, design/16 §4, design/19 §2): with nothing
// selected the DM's panel shows the scene — the map, the grid setup,
// snapping, the undelete — and the things on the table; with something
// selected, that thing: its name, size in squares and free size, whether its
// art stretches or sits inside, its layer, its lock, hidden or shown, its
// owners, its numbers and what of them the other players see, duplication,
// deletion. Several selected: reveal or hide them together (one event, so an
// ambush lands as one), with a reminder when that is part of a numbered
// group. A player's panel lists what they have been shown; for one of
// theirs it offers a step, their numbers, and the sharing record — ONE
// record, rendered here and nowhere else (design/19 §2). It always answers:
// never blank.
// Issue 1575 puts all of it in sections (`PanelParts.tsx`), remembered open per person and per kind of token, and a
// player shown someone else's token reads "Unknown creature" until the DM tells a field or reveals a fact.
import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { Area, AssetInfo, ArtFit, Disposition, DmObject, Effect, EventBody, Fact, Field, Layer, Light, LightLevel, Sense, VisionRule, Marker, PlayerObject, Shares, Size, Values, WoundLevel } from '@dragons-table/vtt-shared';
import { DEAD_PAST, woundsOf, NONLETHAL_WORDS, nonlethalStatus, sameSoleOwner, DISPOSITIONS, SIDE_MARKER, EMPTY_GRID, FIELDS, MARKERS, dispositionOf, OVERLAY_KEYS, boardExtent, combatantOf, defaultShares, emptyEncounter, mayClearArea, mayClearFocus, numberedMates, previewRefusal, tokenLabel, snapToCells, stepCell, TORCH, VISION_LIMITS, lightFromEntry, TIMES_OF_DAY, timeOfDay, floorOf } from '@dragons-table/vtt-shared';
import { entryLight, searchLights, type CompendiumHit } from '../../compendium';
import { focusRect } from '../../board/focusArrows';
import { intersectionAt } from '../../board/areaGeometry';
import type { Encounter, Overlays, PlayerEncounter, TableMember } from '@dragons-table/vtt-shared';
import { areaSizeLabel, caughtBy, conditionLevelKey, elevation, conditionWithLevel, memberLabel, type NamingObject } from '@dragons-table/vtt-shared';
import { listAssets, setTableMap } from '../../assets';
import { useConditionSuggestions } from '../../conditions';
import { markerLabel } from '../../board/tokenStatus';
import { boardBus } from '../../board/bus';
import { selection } from '../../board/selection';
import { moveRev } from '../../board/group';
import { squareOf, squareWords } from '../../board/keyStep';
import { saveSettings } from '../../layout';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { healthOf, hpWords, useHealthResource, useNumberWords, useWoundRules, woundRulesOf, type NumberWords } from '../../table/numberWords';
import { useShell } from '../context';
import { useLook } from '../useLook';
import { useBoardMap } from '../../board/sceneMap';
import { GridSetup } from './GridSetup';
import { gridKept } from '../gridKept';
import { TokenCharacter } from './CharacterParts';
import { NOT_ON_SHEET } from '../../characters';
import { TokenActions } from './ActionParts';
import { CombatRulesSetting, OverrideSetting } from './CombatRules';
import { RulingsSection } from './Rulings';
import { KnownFacts, TokenEntry, revealedCount, useInferredLines, useStatBlock } from './EntryParts';
import { ConditionChips, HpBar, Portrait, Section, StatTiles } from './PanelParts';
import { panelKindOf, panelPrefs, usePanelPrefs } from '../panelPrefs';
import { MatchSwatch, useColourMatch, type Matched } from '../colourMatch';
import { useTarget } from '../../board/target';
import { entryPageUrl } from '../../entries';
import { ResolveSaves } from './SaveParts';
import { AurasControl } from './Auras';
import { CyberPanel, HostEditor } from './MatrixParts';

const btn = 'rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800 disabled:opacity-50 aria-pressed:border-amber-500 aria-pressed:text-amber-200';
const num = 'w-14 rounded bg-stone-800 px-1 py-0.5 text-xs';
type Thing = PlayerObject | DmObject;

function useSelection(): ReadonlySet<string> {
  const [s, setS] = useState(selection.get());
  useEffect(() => selection.subscribe(setS), []);
  return s;
}

function MapChooser({ onClose }: { onClose: () => void }) {
  const { campaignId, onResult, table } = useShell();
  const boardMap = useBoardMap(); // the scene on this screen (issue 834)
  const { scene, store } = useTableState();
  const [assets, setAssets] = useState<AssetInfo[] | null>(null);
  if (assets === null) { void listAssets().then(setAssets); }
  const pick = async (id: string | null) => {
    const result = await setTableMap(campaignId, id, scene?.id ?? null);
    onResult(result);
    if (result.kind === 'ok') gridKept.mapChanged(store.state.grid);
    onClose();
  };
  return (
    <div className="mt-2 space-y-1 rounded border border-stone-700 p-2" data-testid="map-chooser">
      <p className="text-xs text-stone-400">From your library. Changing the map keeps the grid; the old map stays in the library.</p>
      {assets === null && <p className="text-xs text-stone-500">Loading…</p>}
      {assets !== null && assets.length === 0 && <p className="text-xs text-stone-500">Nothing uploaded yet.</p>}
      <ul className="max-h-48 overflow-auto">
        {(assets ?? []).map((a) => (
          <li key={a.id}>
            <button type="button" className="w-full rounded px-1 py-0.5 text-left text-sm hover:bg-stone-800 disabled:opacity-50" disabled={boardMap?.id === a.id} onClick={() => pick(a.id)}>
              {a.name} <span className="text-xs text-stone-500">{a.kind} · {a.width} × {a.height}</span>
            </button>
          </li>
        ))}
      </ul>
      {/* Upload from here (the 2026-10-15 session pass): an empty library only said where the Library was. */}
      <button type="button" className="rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800" onClick={() => { boardBus.showTool('library'); onClose(); }}>Upload a map in the Library…</button>
      <div className="flex gap-2">
        {boardMap && <button type="button" className={btn} onClick={() => pick(null)}>No map (empty grid)</button>}
        <button type="button" className={btn} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

const SIZES: Array<{ label: string; size: Size }> = [
  { label: '1 × 1', size: { w: 1, h: 1 } }, { label: '2 × 2', size: { w: 2, h: 2 } }, { label: '3 × 3', size: { w: 3, h: 3 } }, { label: '4 × 4', size: { w: 4, h: 4 } },
];

/** A field's name, the numbers in the system's words (issue 1309). */
const fieldLabels = (w: NumberWords): Record<Field, string> => ({ name: 'Name', hp: w.health, ac: w.defence, slot1: 'Slot 1', slot2: 'Slot 2', initBonus: 'Initiative bonus', initRoll: 'Initiative roll', conditions: 'Conditions', resources: 'Resources', side: 'Side', stops: 'Damage defences' });

/** The numbers as one line, for anyone allowed to see some of them, in the system's words (issue 1309) and its resources' (issue 1312). */
export function valuesLine(v: Values, w: NumberWords, resources: ReadonlyArray<{ key: string; label: string; levels?: WoundLevel[]; end?: true }> = [], health: string | null = null): string {
  const parts: string[] = [];
  if (v.hp) parts.push(hpWords(v.hp, w));
  for (const r of resources) {
    const have = v.resources?.[r.key];
    if (have) parts.push(`${r.label} ${have.current}${have.max !== undefined ? `/${have.max}` : ''}`);
  }
  // The wound those numbers make (issue 1526): only from the numbers this viewer is sent.
  const wounds = woundsOf(woundRulesOf(resources, health), v.resources).words;
  if (wounds.length) parts.push(wounds.join(', '));
  if (v.ac !== undefined) parts.push(`${w.defence} ${v.ac}`);
  if (v.slot1) parts.push(`${v.slot1.label} ${v.slot1.value}${v.slot1.max !== undefined ? `/${v.slot1.max}` : ''}`);
  if (v.slot2) parts.push(`${v.slot2.label} ${v.slot2.value}${v.slot2.max !== undefined ? `/${v.slot2.max}` : ''}`);
  if (v.initBonus !== undefined) parts.push(`${w.initiative} ${v.initBonus >= 0 ? '+' : ''}${v.initBonus}`);
  if (v.initRoll !== undefined) parts.push(`rolled ${v.initRoll}`);
  if (v.conditions && v.conditions.length) parts.push(v.conditions.map((c) => conditionWithLevel(c, v.condition_levels)).join(', '));
  return parts.join(' · ');
}

/** Who a thing belongs to (design/13 §7): the DM always, and the player owner where there is one — named in the panel, never painted on the board. */
export function ownersLine(o: NamingObject & { owners: string[] }, members: TableMember[], meId: string, objects: Record<string, NamingObject> = {}): string {
  // By character and handle (issue 1023), the character this token stands for (issue 1494); an owner no longer at the
  // table is a former player.
  const name = (m: TableMember | undefined) => memberLabel(m, objects, { meId, you: 'you', about: o });
  const dm = members.find((m) => m.role === 'dm');
  const players = o.owners.filter((id) => id !== dm?.id).map((id) => name(members.find((m) => m.id === id)));
  const dmLabel = dm ? name(dm) : 'the DM';
  const dmName = dmLabel === 'the DM' ? 'the DM' : `the DM (${dmLabel})`;
  return players.length ? `Belongs to ${dmName} and ${players.join(', ')}.` : `Belongs to ${dmName}.`;
}

const overlayLabels = (w: NumberWords): Record<keyof Overlays, string> => ({ hp: `${w.health} bar`, ac: `${w.defence} shield`, slot1: 'Slot 1', slot2: 'Slot 2', conditions: 'Conditions' });

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number.isFinite(Number(s)) ? Math.trunc(Number(s)) : null);

/**
 * The fields a token's sharing offers: its resources only on a system that declares them (issue 1312); its side only to
 * the DM (qa-runs#1157), whose alone it is to reveal.
 */
const useShareFields = (resources: boolean, side: boolean): readonly Field[] => useMemo(() => FIELDS.filter((f) => (f !== 'resources' || resources) && (f !== 'side' || side)), [resources, side]);

/** The sharing record's checkboxes: what the other players see of this token. The DM's, or the owner's for their own. */
function SharesEditor({ id, shares, fields }: { id: string; shares: Shares; fields: readonly Field[] }) {
  const { act } = useTableState();
  const FIELD_LABELS = fieldLabels(useNumberWords());
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="shares">
      <span className="text-stone-400">Shown to other players:</span>
      {fields.map((f) => (
        <label key={f} className="flex items-center gap-1">
          <input type="checkbox" aria-label={`Show ${FIELD_LABELS[f]} to other players`} checked={shares[f]} onChange={(e) => act({ kind: 'object.share', id, shares: { [f]: e.target.checked } })} />
          {FIELD_LABELS[f]}
        </label>
      ))}
    </div>
  );
}

/** Dead or not (issue 855): the DM for any token, an owner for their own. Greyed and crossed on the board; it stays in the turn order. */
function DeadToggle({ o }: { o: { id: string; dead: boolean; concentrating?: { name: string } | undefined } }) {
  const { act } = useTableState();
  return (
    <>
      <label className="flex items-center gap-1 text-xs" title="Drawn greyed with a cross. It stays on the board and in the turn order.">
        <input type="checkbox" aria-label="Dead" checked={o.dead} onChange={(e) => act({ kind: 'object.dead', id: o.id, dead: e.target.checked })} /> dead
      </label>
      {o.concentrating && (
        <span className="flex items-center gap-1 text-xs" data-testid="concentrating">
          {`Concentrating on ${o.concentrating.name}`}
          <button type="button" className="rounded border border-stone-600 px-1" onClick={() => act({ kind: 'object.concentrating', id: o.id, holding: null })}>End</button>
        </span>
      )}
    </>
  );
}

/** Degrees 0–359 from any whole number (issue 1030): -90 is 270, 400 is 40. */
export const wrapDegrees = (d: number): number => ((Math.round(d) % 360) + 360) % 360;

/**
 * A token's floor and height (qa-runs#1160; design/32 §2): the DM moves it to any floor of the scene; whoever may move
 * it gives it a height in the system's unit above the scene's ground (blank: standing on its floor). Each is a move
 * where it stands, so the server counts the height in the move and refuses what it should.
 */
function HeightControl({ o }: { o: { id: string; x: number; y: number; rev?: number; floor?: string; z?: number } }) {
  const { act, store, systemRules } = useTableState();
  const mayAct = useMayAct();
  const d = systemRules.distance;
  const per = d?.perSquare ?? 1;
  const floors = store.state.scene.floors ?? [];
  const now = typeof o.z === 'number' ? String(Math.round(o.z * per * 100) / 100) : '';
  const [draft, setDraft] = useState(now);
  useEffect(() => setDraft(now), [now]);
  const commit = () => {
    if (draft === now) return;
    const n = Number(draft);
    if (draft.trim() !== '' && !Number.isFinite(n)) { setDraft(now); return; }
    act({ kind: 'object.move', id: o.id, route: [{ x: o.x, y: o.y }], rev: o.rev ?? 0, z: draft.trim() === '' ? null : n / per });
  };
  const floor = floors.length > 1 ? floorOf(o, floors) : null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" role="group" aria-label="Height" data-testid="height" title="Its height above the scene's ground; blank, it stands on its floor. Distance counts it.">
      {floor && mayAct('scene.set') && (
        <label className="flex items-center gap-1">Floor:
          <select aria-label="Floor" className="rounded border border-stone-700 bg-stone-900 px-1" value={floor}
            onChange={(e) => act({ kind: 'object.move', id: o.id, route: [{ x: o.x, y: o.y }], rev: o.rev ?? 0, floor: e.target.value })}>
            {floors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </label>
      )}
      <span className="text-stone-400">Height:</span>
      <input type="number" aria-label="Height" className="w-16 rounded border border-stone-700 bg-stone-900 px-1" step={per} placeholder="standing"
        value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />{d?.unit ?? 'squares'}
    </div>
  );
}

/**
 * Turning a token (issue 1030, TOKEN-009): the DM any, an owner their own. 45° steps either way and a free number;
 * the art turns on the board and the footprint stays as it is. The server decides; a refusal is said as usual.
 */
function RotateControl({ o }: { o: { id: string; rotation?: number } }) {
  const { act } = useTableState();
  const now = o.rotation ?? 0;
  const [draft, setDraft] = useState(String(now));
  useEffect(() => setDraft(String(now)), [now]);
  const commit = () => {
    const n = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(n)) { setDraft(String(now)); return; }
    act({ kind: 'object.rotate', id: o.id, rotation: wrapDegrees(n) });
  };
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" role="group" aria-label="Rotate" data-testid="rotate" title="The picture turns; the squares it stands on do not.">
      <span className="text-stone-400">Rotate:</span>
      <button type="button" className={btn} aria-label="Rotate left 45°" onClick={() => act({ kind: 'object.rotate', id: o.id, rotation: wrapDegrees(now - 45) })}>⟲ 45°</button>
      <button type="button" className={btn} aria-label="Rotate right 45°" onClick={() => act({ kind: 'object.rotate', id: o.id, rotation: wrapDegrees(now + 45) })}>⟳ 45°</button>
      <input type="number" aria-label="Rotation in degrees" className="w-16 rounded border border-stone-700 bg-stone-900 px-1" step={1}
        value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />°
    </div>
  );
}

/**
 * A token's light (issue 1145; design/30 §1.4): the DM's alone, or a co-DM's with the tokens power (#1144). Bright and dim radii in squares
 * (dim is the edge of all its light) and a switch; the one preset is the design's own 3.5 torch, 20 ft bright and 40 ft
 * shadowy. No rules value is data here (§8): the numbers are what is typed. The server decides; a refusal is said as usual.
 */
export function LightControl({ o }: { o: { id: string; light?: Light } }) {
  const { act, systemRules } = useTableState();
  const now = o.light;
  const [picking, setPicking] = useState(false);
  const [bright, setBright] = useState(String(now?.bright ?? TORCH.bright));
  const [dim, setDim] = useState(String(now?.dim ?? TORCH.dim));
  useEffect(() => { setBright(String(now?.bright ?? TORCH.bright)); setDim(String(now?.dim ?? TORCH.dim)); }, [now?.bright, now?.dim]);
  const send = (light: Light | null) => act({ kind: 'object.light', id: o.id, light });
  const commit = () => {
    const b = Number(bright); const d = Number(dim);
    if (!Number.isFinite(b) || !Number.isFinite(d) || b < 0 || d <= 0 || b > d) { setBright(String(now?.bright ?? TORCH.bright)); setDim(String(now?.dim ?? TORCH.dim)); return; }
    send({ bright: b, dim: d, on: now?.on ?? true });
  };
  const field = 'w-12 rounded border border-stone-700 bg-stone-900 px-1';
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" role="group" aria-label="Light" data-testid="token-light" title="The light it carries, in squares: bright out to the first number, dim out to the second. Walls block it. It matters with the scene's vision on in a dim or dark scene.">
      <label className="flex items-center gap-1 text-stone-400">
        <input type="checkbox" aria-label="Carries a light" checked={!!now?.on} onChange={(e) => send({ bright: now?.bright ?? Number(bright), dim: now?.dim ?? Number(dim), on: e.target.checked })} /> Light:
      </label>
      <input type="number" aria-label="Bright light, squares" className={field} min={0} step={0.5} value={bright} onChange={(e) => setBright(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
      <span className="text-stone-400">bright,</span>
      <input type="number" aria-label="Dim light, squares" className={field} min={0.5} step={0.5} value={dim} onChange={(e) => setDim(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
      <span className="text-stone-400">dim</span>
      <button type="button" className={btn} title="A torch: 20 ft bright, 40 ft shadowy (4 and 8 squares)." onClick={() => send({ ...TORCH })}>Torch</button>
      {now && <button type="button" className={btn} aria-label="Take the light away" onClick={() => send(null)}>None</button>}
      <button type="button" className={btn} aria-expanded={picking} title="Take the light a spell or item sheds, from the rules." onClick={() => setPicking((p) => !p)}>From a spell or item…</button>
      {now?.from && <span className="w-full text-stone-300" data-testid="light-from">{lightFromLine(now, systemRules.distance)}</span>}
      {picking && <LightFromEntry onPick={(light) => { send(light); setPicking(false); }} />}
    </div>
  );
}

/** "Light (spell): 20/40 ft" — the entry's name and kind, then its light, in the system's unit (squares with none). */
export function lightFromLine(l: Light, distance: { unit: string; perSquare: number } | null): string {
  const what = l.from!.segment.replace(/s$/, '').replace(/[-_]/g, ' ');
  const n = (sq: number) => (distance ? Math.round(sq * distance.perSquare * 100) / 100 : sq);
  return `${l.from!.name} (${what}): ${n(l.bright)}/${n(l.dim)} ${distance ? distance.unit : 'squares'}`;
}

/**
 * Pick a spell or item whose rules say it sheds light (issue 1151): a search of the families that carry a light, and a
 * pick reads its light from the server and turns it into squares (rounded down) for the DM to set.
 */
export function LightFromEntry({ onPick }: { onPick: (light: Light) => void }) {
  const { campaignId } = useShell();
  const { systemRules } = useTableState();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CompendiumHit[]>([]);
  const [said, setSaid] = useState('');
  useEffect(() => {
    let live = true;
    const text = q.trim();
    if (!text) { setHits([]); return undefined; }
    const t = setTimeout(() => {
      searchLights(campaignId, text).then((a) => { if (!live) return; if (a.kind === 'ok') { setHits(a.hits); setSaid(a.hits.length ? '' : 'Nothing that sheds light matches.'); } else setSaid('The rules cannot be searched just now.'); }).catch(() => undefined);
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, campaignId]);
  const pick = async (h: CompendiumHit) => {
    const a = await entryLight(campaignId, h.segment, h.slug).catch(() => ({ kind: 'error' as const }));
    if (a.kind === 'refused') return setSaid(a.reason);
    if (a.kind === 'error') return setSaid('The rules cannot be read just now.');
    if (!a.entry.light) return setSaid(`${a.entry.name} sheds no light.`);
    const light = lightFromEntry(a.entry.light, systemRules.distance, { name: a.entry.name, segment: a.entry.segment, slug: a.entry.slug });
    if (!light) return setSaid(systemRules.distance ? `${a.entry.name}'s light reaches less than a square.` : 'This game system has no distance rule, so its light cannot be measured out.');
    setSaid('');
    onPick(light);
  };
  return (
    <div className="flex w-full flex-col gap-1" data-testid="light-from-entry">
      <input type="search" aria-label="Spell or item that sheds light" placeholder="Light, torch, sunrod…" className="w-full rounded border border-stone-700 bg-stone-900 px-1" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      {hits.length > 0 && (
        <ul className="max-h-40 overflow-y-auto" aria-label="Spells and items that shed light">
          {hits.map((h) => (
            <li key={`${h.segment}/${h.slug}`}><button type="button" className="w-full rounded px-1 text-left hover:bg-stone-800" onClick={() => { void pick(h); }}>{h.name} <span className="text-stone-500">{h.family}</span></button></li>
          ))}
        </ul>
      )}
      {said && <span className="text-amber-300" role="status">{said}</span>}
      <span className="text-stone-500">Its radii are turned into squares, rounded down.</span>
    </div>
  );
}

/**
 * A token's vision (issue 1146; design/30 §1.5): the DM's alone, or a co-DM's with the tokens power (#1144). One row per kind of vision the
 * table's system declares, by its label, with a range in squares where the kind has one (darkvision, blindsight) and
 * none where it has not (low-light vision). The whole list is sent; the server stamps each kind from the system.
 */
export function VisionControl({ o }: { o: { id: string; vision?: Sense[] } }) {
  const { act, systemRules } = useTableState();
  const kinds = systemRules.vision;
  const now = o.vision ?? [];
  const [ranges, setRanges] = useState<Record<string, string>>({});
  useEffect(() => { setRanges(Object.fromEntries(now.filter((x) => x.range !== null).map((x) => [x.key, String(x.range)]))); }, [JSON.stringify(now)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!kinds.length) return null;
  const per = systemRules.distance;
  const send = (vision: Sense[]) => act({ kind: 'object.vision', id: o.id, vision });
  const entry = (k: VisionRule, range: number | null): Sense => ({ key: k.key, kind: k.kind, range, ...(k.multiplier !== undefined ? { multiplier: k.multiplier } : {}) });
  const rangeOf = (k: VisionRule): number | null => {
    const v = Number(ranges[k.key] ?? '');
    return Number.isFinite(v) && v > 0 && v <= VISION_LIMITS.range ? v : null;
  };
  const toggle = (k: VisionRule, on: boolean) => {
    const rest = now.filter((x) => x.key !== k.key);
    if (!on) return send(rest);
    const range = k.kind === 'light_multiplier' ? null : rangeOf(k) ?? (per ? 60 / per.perSquare : 12);
    send([...rest, entry(k, range)]);
  };
  const commit = (k: VisionRule) => {
    const range = rangeOf(k);
    const had = now.find((x) => x.key === k.key);
    if (range === null) { setRanges((r) => ({ ...r, [k.key]: had?.range != null ? String(had.range) : '' })); return; }
    if (had?.range === range) return;
    send([...now.filter((x) => x.key !== k.key), entry(k, range)]);
  };
  const field = 'w-12 rounded border border-stone-700 bg-stone-900 px-1';
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" role="group" aria-label="Vision" data-testid="token-vision"
      title={`What it sees beyond normal sight, by this game system's kinds of vision. Ranges are in squares${per ? ` (a square is ${per.perSquare} ${per.unit})` : ''}. It matters with the scene's vision on in the dark.`}>
      <span className="text-stone-400">Vision:</span>
      {kinds.map((k) => {
        const had = now.find((x) => x.key === k.key);
        return (
          <span key={k.key} className="flex items-center gap-1">
            <label className="flex items-center gap-1"><input type="checkbox" aria-label={k.label} checked={!!had} onChange={(e) => toggle(k, e.target.checked)} /> {k.label}</label>
            {k.kind !== 'light_multiplier' && had && (
              <input type="number" aria-label={`${k.label} range, squares`} className={field} min={0.5} step={0.5} value={ranges[k.key] ?? ''}
                onChange={(e) => setRanges((r) => ({ ...r, [k.key]: e.target.value }))} onBlur={() => commit(k)} onKeyDown={(e) => { if (e.key === 'Enter') commit(k); }} />
            )}
          </span>
        );
      })}
    </div>
  );
}

/** The tokens `o` may follow (issue 1031): from this viewer's store alone — tokens one player owns alone with it, or the DM's own. */
export function followCandidates(o: { id: string; kind: string; owners?: string[] }, objects: Record<string, { id: string; kind: string; name: string; owners?: string[] }>, isDm: boolean) {
  // A player links only their own: a token of the DM's alone is not theirs to link.
  if (o.kind !== 'token' || !o.owners || (!isDm && o.owners.length !== 1)) return [];
  return Object.values(objects).filter((c) => c.id !== o.id && c.kind === 'token' && c.owners && sameSoleOwner({ owners: o.owners! }, { owners: c.owners }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * What a token follows (issue 1031, TOKEN-020): a familiar follows its owner. The DM, or the player who owns both.
 * The list is a preview from the store; the server's `mayLink` is the rule, and its refusal is said like any other.
 */
export function FollowPicker({ o }: { o: { id: string; kind: string; owners?: string[]; follows?: string; mounted?: true } }) {
  const { store, act, viewingAs } = useTableState();
  const mayAct = useMayAct();
  const options = followCandidates(o, store.state.objects as never, mayAct('object.follow') && !viewingAs);
  if (!options.length && !o.follows) return null;
  return (
    <label className="flex flex-wrap items-center gap-1 text-xs" data-testid="follow-picker" title="It moves with the token it follows.">
      <span className="text-stone-400">Follows:</span>
      <select aria-label="Follows" className="rounded border border-stone-700 bg-stone-900 px-1" value={o.follows ?? ''}
        onChange={(e) => act({ kind: 'object.follow', id: o.id, follows: e.target.value || null })}>
        <option value="">Nothing</option>
        {options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {o.follows && (
        <span className="inline-flex items-center gap-1" title="It rides the token it follows: a hit on that token is this one's to answer.">
          <input type="checkbox" aria-label="Riding it" data-testid="follow-mounted" checked={!!o.mounted}
            onChange={(e) => act({ kind: 'object.follow', id: o.id, follows: o.follows!, ...(e.target.checked ? { mounted: true } : {}) } as never)} />
          <span className="text-stone-400">Riding it</span>
        </span>
      )}
    </label>
  );
}

const SIDE_WORDS: Record<Disposition, string> = { friend: 'Friend', neutral: 'Neutral', foe: 'Foe' };

/**
 * Which side a token is on (issue 1046; qa-runs#1157): a three-position switch, Friend · Neutral · Foe, the DM slides
 * freely (a click on a position, or the arrow keys). Its default is the token's (a player's a friend, any other a
 * foe). A player is sent their own tokens' sides, and another's once the DM reveals it (Players see: Side); the rules
 * and the turn tracker read it.
 */
function DispositionPicker({ o }: { o: DmObject }) {
  const { act } = useTableState();
  const look = useLook();
  const side = dispositionOf(o);
  const at = DISPOSITIONS.indexOf(side);
  const set = (d: Disposition) => { if (d !== side) act({ kind: 'object.disposition', id: o.id, disposition: d }); };
  const onKey = (e: KeyboardEvent) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    set(DISPOSITIONS[Math.max(0, Math.min(DISPOSITIONS.length - 1, at + step))]!);
  };
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="disposition" title="Players see their own tokens' sides, and another's only once you reveal it under Players see. The rules and the turn tracker read it.">
      <span className="text-stone-400" id={`side-${o.id}`}>Side:</span>
      <div role="radiogroup" aria-labelledby={`side-${o.id}`} onKeyDown={onKey} className="relative inline-flex rounded-full border border-stone-600 bg-stone-800 p-0.5">
        <span aria-hidden="true" data-testid="side-thumb" className="absolute inset-y-0.5 rounded-full transition-[left] duration-150"
          style={{ width: 'calc((100% - 0.25rem) / 3)', left: `calc(0.125rem + ${at} * (100% - 0.25rem) / 3)`, backgroundColor: look.marker(SIDE_MARKER[side]) }} />
        {DISPOSITIONS.map((d) => (
          <button key={d} type="button" role="radio" aria-checked={side === d} tabIndex={side === d ? 0 : -1}
            className={`relative z-10 min-w-[3.75rem] rounded-full px-2 py-0.5 ${side === d ? 'font-semibold text-white' : 'text-stone-300 hover:text-white'}`}
            onClick={() => set(d)}>{SIDE_WORDS[d]}</button>
        ))}
      </div>
    </div>
  );
}

/**
 * Players work out its numbers (issue 1133; design/29 §6): the DM's switch, per creature, off until turned on. On, a
 * player who can see it is shown the AC range and damage the attacks they saw imply; the DM sees it either way.
 */
function InferToggle({ o }: { o: DmObject }) {
  const { act } = useTableState();
  const worked = useInferredLines(o);
  return (
    <div className="space-y-0.5 text-xs" data-testid="infer-toggle">
      <label className="flex items-center gap-1" title="Players see what the attacks they were shown say of its numbers.">
        <input type="checkbox" aria-label="Players work out its numbers" checked={o.infer === true} onChange={(e) => act({ kind: 'object.infer', id: o.id, on: e.target.checked })} /> Players work out its numbers
      </label>
      {worked.length > 0 && <p className="text-stone-400" data-testid="dm-inferred">{o.infer ? 'They see: ' : 'They would see: '}{worked.join(' · ')}</p>}
    </div>
  );
}

/**
 * No extra attacks (qa-runs#1159): the DM turns off, for one creature, what its entries add to its attacks — Manyshot,
 * Whirlwind Attack, flurry of blows, the two-weapon feats' and haste's swings, Great Cleave — as attacks of opportunity
 * are turned off. The DM's alone: no player is sent it.
 */
function NoExtraToggle({ o }: { o: DmObject }) {
  const { act } = useTableState();
  const on = (o as { noExtra?: true }).noExtra === true;
  return (
    <label className="flex items-center gap-1 text-xs" data-testid="noextra-toggle" title="Manyshot, Whirlwind Attack, flurry, extra swings from feats or haste, Great Cleave: off for this creature while this is on.">
      <input type="checkbox" aria-label="No extra attacks from feats" checked={on} onChange={(e) => act({ kind: 'object.noextra', id: o.id, on: e.target.checked } as never)} /> No extra attacks from feats
    </label>
  );
}

/**
 * No attacks of opportunity (issue 1500, Tom's A on #1500): every creature in an encounter takes them; the DM turns them
 * off for one that cannot (no melee reach, flat-footed). Its owner reads it on their own token (`NoAooNote`).
 */
function NoAooToggle({ o }: { o: DmObject }) {
  const { act } = useTableState();
  return (
    <label className="flex items-center gap-1 text-xs" data-testid="noaoo-toggle" title="Every creature in an encounter takes attacks of opportunity unless this is on. Its player sees it; nobody else does.">
      <input type="checkbox" aria-label="No attacks of opportunity" checked={o.noAoo === true} onChange={(e) => act({ kind: 'object.noaoo', id: o.id, on: e.target.checked })} /> No attacks of opportunity
    </label>
  );
}

/** What an owner is told of their own token's attacks of opportunity (issue 1500): only when the DM turned them off. */
/**
 * A token's astral state (issue 1531; docs/MRCS/shadowrun-3.md §9 step 17): none, or one of the system's states, set by
 * the DM or the token's owner. Shown to them alone, as the server sends it to them alone; the board draws no astral
 * plane, and the server enforces who may attack whom.
 */
export function AstralControl({ o }: { o: { id: string; astral?: string } }) {
  const { act, setNotice, systemRules } = useTableState();
  const view = systemRules.astral;
  if (!view) return null;
  const now = view.states.find((s) => s.key === o.astral);
  return (
    <label className="flex items-center gap-1 text-xs" data-testid="astral-control" title="On the astral plane: perceiving keeps its body, projecting leaves it. Only the DM and its player see this.">
      Astral
      <select aria-label="Astral state" className="rounded bg-stone-800 px-1 py-0.5 text-xs" value={o.astral ?? ''} onChange={(e) => act({ kind: 'object.astral', id: o.id, state: e.target.value || null } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That astral state could not be set.'); })}>
        <option value="">None</option>
        {view.states.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
      {now?.only && <span className="text-stone-400" data-testid="astral-only">on the astral plane alone: only astral attacks reach it</span>}
    </label>
  );
}

/**
 * Jacking in (issue 1173; docs/MRCS/shadowrun-3.md §16): a switch on a decker's token, shown on a system that names one.
 * On, its combatant throws the Matrix initiative from the sheet; off, its own again. The DM or the token's owner, and
 * shown to them alone.
 */
export function JackControl({ o }: { o: { id: string; jacked?: true } }) {
  const { act, setNotice, systemRules } = useTableState();
  const label = systemRules.matrix?.jackIn;
  if (!label) return null;
  return (
    <label className="flex min-h-[2.25rem] items-center gap-2 text-xs" data-testid="jack-control" title="In the Matrix: the turn order uses the decker's Matrix initiative. Only the DM and its player see this.">
      <input type="checkbox" className="h-4 w-4" checked={Boolean(o.jacked)} onChange={(e) => act({ kind: 'object.jack', id: o.id, on: e.target.checked } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be changed.'); })} />
      {label}
    </label>
  );
}

function NoAooNote({ o }: { o: { noAoo?: true } }) {
  return o.noAoo ? <p className="text-xs text-stone-400" data-testid="noaoo-note">No attacks of opportunity (the DM's setting).</p> : null;
}

/** The marker ring's colour (issue 855), the DM's: one of the shared palette, or none. */
function MarkerSwatches({ o }: { o: { id: string; marker: Marker | null } }) {
  const { act } = useTableState();
  const look = useLook();
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="marker-swatches">
      <span className="text-stone-400">Marker:</span>
      {MARKERS.map((m) => (
        <button key={m} type="button" aria-label={markerLabel(m)} aria-pressed={o.marker === m} title={markerLabel(m)}
          className={`h-5 w-5 rounded-full border-2 ${o.marker === m ? 'border-stone-100' : 'border-stone-700'}`} style={{ backgroundColor: look.marker(m) }}
          onClick={() => act({ kind: 'object.marker', id: o.id, marker: m })} />
      ))}
      <button type="button" aria-label="No marker" aria-pressed={o.marker === null} className={btn} onClick={() => act({ kind: 'object.marker', id: o.id, marker: null })}>none</button>
    </div>
  );
}

/**
 * The numbers. The DM edits every field; an owner their current HP and the
 * slots' values (decisions 13, 26, 35) — the maximum, the labels, AC and the
 * initiative stay the DM's, and the server holds the same line.
 */
function ValuesEditor({ o, full }: { o: { id: string; values: Values; hpOffSheet?: true }; full: boolean }) {
  const { act, systemRules } = useTableState();
  // The system's other defences (qa-runs 402), whatever its data declares — each a box
  // beside the AC, holding what the DM typed at an attack's prompt (or here); empty is none, and an attack asks again.
  const otherDefences = (systemRules.attack?.defences ?? []).filter((d) => d.key !== systemRules.attack?.main);
  const [defs, setDefs] = useState<Record<string, string>>({});
  const w = useNumberWords();
  const v = o.values;
  const [hpCur, setHpCur] = useState(''); const [hpMax, setHpMax] = useState(''); const [ac, setAc] = useState('');
  const [nl, setNl] = useState('');
  const [tempHp, setTempHp] = useState(v.hp?.temp ? String(v.hp.temp) : '');
  const [stopsText, setStopsText] = useState(v.stops ?? '');
  const [s1l, setS1l] = useState(''); const [s1v, setS1v] = useState(''); const [s2l, setS2l] = useState(''); const [s2v, setS2v] = useState('');
  const [s1m, setS1m] = useState(''); const [s2m, setS2m] = useState('');
  const [ib, setIb] = useState(''); const [ir, setIr] = useState(''); const [cond, setCond] = useState('');
  // The levels of counted conditions (`condition_reads`' `levels`; docs/MRCS/dnd-5e.md §3.4): a box beside each condition.
  const [levels, setLevels] = useState<Record<string, string>>({});
  const countsLevels = systemRules.conditionReads?.includes('levels') === true;
  const [spd, setSpd] = useState('');
  const [fly, setFly] = useState('');
  const [adding, setAdding] = useState('');
  const { campaignId } = useShell();
  // The site's conditions list for the table's system, as suggestions (issue 855); free text always works.
  const suggestions = useConditionSuggestions(campaignId, full);
  const listId = `conditions-${o.id}`;
  const addCondition = () => {
    const name = adding.trim().slice(0, 40);
    if (!name) return;
    const have = v.conditions ?? [];
    setAdding('');
    if (have.some((c) => c.toLowerCase() === name.toLowerCase()) || have.length >= 20) return;
    act({ kind: 'object.values', id: o.id, values: { conditions: [...have, name] } });
  };
  // Each box follows its own number only (qa-runs 143): a condition added, or anyone's change to another number, leaves
  // what was typed and not yet saved where it is. Compared by value, since a change elsewhere may bring an equal copy.
  const key = (x: unknown) => JSON.stringify(x ?? null);
  useEffect(() => {
    setHpCur(v.hp ? String(v.hp.current) : ''); setHpMax(v.hp ? String(v.hp.max) : ''); setNl(v.hp?.nonlethal ? String(v.hp.nonlethal) : ''); setTempHp(v.hp?.temp ? String(v.hp.temp) : '');
  }, [o.id, key(v.hp)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAc(v.ac !== undefined ? String(v.ac) : ''); }, [o.id, v.ac]);
  useEffect(() => { setStopsText(v.stops ?? ''); }, [o.id, v.stops]);
  useEffect(() => {
    setDefs(Object.fromEntries(Object.entries(v.defences ?? {}).map(([k, n]) => [k, String(n)])));
  }, [o.id, key(v.defences)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setS1l(v.slot1?.label ?? ''); setS1v(v.slot1 ? String(v.slot1.value) : ''); setS1m(v.slot1?.max !== undefined ? String(v.slot1.max) : '');
  }, [o.id, key(v.slot1)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setS2l(v.slot2?.label ?? ''); setS2v(v.slot2 ? String(v.slot2.value) : ''); setS2m(v.slot2?.max !== undefined ? String(v.slot2.max) : '');
  }, [o.id, key(v.slot2)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setIb(v.initBonus !== undefined ? String(v.initBonus) : ''); }, [o.id, v.initBonus]);
  useEffect(() => { setIr(v.initRoll !== undefined ? String(v.initRoll) : ''); }, [o.id, v.initRoll]);
  useEffect(() => { setSpd(v.speed !== undefined ? String(v.speed) : ''); }, [o.id, v.speed]);
  useEffect(() => { setFly(v.fly !== undefined ? String(v.fly) : ''); }, [o.id, v.fly]);
  useEffect(() => { setCond(v.conditions?.join(', ') ?? ''); }, [o.id, key(v.conditions)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLevels(Object.fromEntries((v.conditions ?? []).map((c) => [conditionLevelKey(c), String(v.condition_levels?.[conditionLevelKey(c)] ?? '')]))); }, [o.id, key(v.conditions), key(v.condition_levels)]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Record<string, unknown> = {};
    const cur = numOrNull(hpCur); const max = full ? numOrNull(hpMax) : (v.hp?.max ?? null);
    // The nonlethal count (issue 1502): the DM's to set or clear (empty is none). Sent only when it changed, so raising the
    // hit points alone heals it as much, by the rules, at the server.
    const count = Math.max(0, Math.trunc(numOrNull(nl) ?? 0));
    const countChanged = full && count !== (v.hp?.nonlethal ?? 0);
    // Temporary hit points: the DM's number replaces the token's, never adds to it (empty is none).
    const temp = Math.max(0, Math.trunc(numOrNull(tempHp) ?? 0));
    const tempChanged = full && temp !== (v.hp?.temp ?? 0);
    const hp = cur !== null && max !== null ? { current: cur, max, ...(countChanged ? { nonlethal: count } : {}), ...(tempChanged ? { temp } : {}) } : null;
    const had = v.hp ? { current: v.hp.current, max: v.hp.max } : null;
    if ((countChanged || tempChanged || JSON.stringify(hp) !== JSON.stringify(had)) && (full || v.hp)) patch.hp = hp;
    if (full && stopsText.trim() !== (v.stops ?? '')) patch.stops = stopsText.trim() || null;
    // A slot's value is the owner's; its label and its maximum (a bar's) are the DM's (decision 35).
    const slot = (label: string, value: string, maxText: string, have: Values['slot1']) => {
      const n = numOrNull(value); const l = full ? label.trim() : (have?.label ?? '');
      const m = full ? numOrNull(maxText) : (have?.max ?? null);
      const next = l && n !== null ? (m !== null && m > 0 ? { label: l, value: n, max: m } : { label: l, value: n }) : null;
      return JSON.stringify(next) !== JSON.stringify(have ?? null) && (full || have) ? next : undefined;
    };
    const p1 = slot(s1l, s1v, s1m, v.slot1); if (p1 !== undefined) patch.slot1 = p1;
    const p2 = slot(s2l, s2v, s2m, v.slot2); if (p2 !== undefined) patch.slot2 = p2;
    if (full) {
      const a = numOrNull(ac); if (a !== (v.ac ?? null)) patch.ac = a;
      // Kept whole: a key the system no longer declares stays as it was; an emptied box clears that defence.
      const nextDefs: Record<string, number> = { ...(v.defences ?? {}) };
      for (const d of otherDefences) {
        const n = numOrNull(defs[d.key] ?? '');
        if (n === null) delete nextDefs[d.key]; else nextDefs[d.key] = Math.trunc(n);
      }
      if (key(Object.keys(nextDefs).length ? nextDefs : null) !== key(v.defences && Object.keys(v.defences).length ? v.defences : null)) patch.defences = Object.keys(nextDefs).length ? nextDefs : null;
      const b = numOrNull(ib); if (b !== (v.initBonus ?? null)) patch.initBonus = b;
      const r = numOrNull(ir); if (r !== (v.initRoll ?? null)) patch.initRoll = r;
      // Its speed (qa-runs#1161): the DM's number over the sheet's; emptied, the sheet's again.
      const sp = numOrNull(spd); if (sp !== (v.speed ?? null)) patch.speed = sp !== null && sp > 0 ? sp : null;
      // Its fly speed (qa-runs#1161): the reachable area's while it is flying; emptied, the sheet's again.
      const fl = numOrNull(fly); if (fl !== (v.fly ?? null)) patch.fly = fl !== null && fl > 0 ? fl : null;
      const c = cond.split(',').map((x) => x.trim()).filter(Boolean);
      if (JSON.stringify(c.length ? c : null) !== JSON.stringify(v.conditions?.length ? v.conditions : null)) patch.conditions = c.length ? c : null;
      if (countsLevels) {
        const nextLevels = Object.fromEntries(c.flatMap((name) => { const n = numOrNull(levels[conditionLevelKey(name)] ?? ''); return n !== null && n >= 1 && n <= 99 ? [[conditionLevelKey(name), Math.trunc(n)]] : []; }));
        if (key(Object.keys(nextLevels).length ? nextLevels : null) !== key(v.condition_levels && Object.keys(v.condition_levels).length ? v.condition_levels : null)) patch.condition_levels = Object.keys(nextLevels).length ? nextLevels : null;
      }
    }
    if (Object.keys(patch).length) act({ kind: 'object.values', id: o.id, values: patch as never });
  };
  return (
    <form className="flex flex-wrap items-center gap-2 text-xs" data-testid="values-editor" onSubmit={submit}>
      <label className="flex items-center gap-1">{w.health}
        <input aria-label={`Current ${w.health}`} type="number" className={num} value={hpCur} disabled={!full && !v.hp} onChange={(e) => setHpCur(e.target.value)} />
        <span>/</span>
        {full ? <input aria-label={`Maximum ${w.health}`} type="number" className={num} value={hpMax} onChange={(e) => setHpMax(e.target.value)} /> : <span>{v.hp?.max ?? '—'}</span>}
        {o.hpOffSheet && <span className="text-amber-300" data-testid="hp-off-sheet-values">({NOT_ON_SHEET})</span>}
      </label>
      {/* Hit points are a current and a maximum together (qa-runs 317): an owner can't start them, and the DM's current
          alone saves nothing, so each is told rather than left with a box that silently keeps its number. */}
      {!full && !v.hp && <span className="text-stone-400" data-testid="hp-needs-max">No {w.health} yet: the DM sets the maximum first.</span>}
      {full && hpCur.trim() !== '' && hpMax.trim() === '' && <span className="text-amber-300" data-testid="hp-needs-max">Give a maximum too, or the {w.health} isn't saved.</span>}
      {full
        ? <label className="flex items-center gap-1">Nonlethal <input aria-label="Nonlethal damage" type="number" min={0} className={num} placeholder="0" value={nl} disabled={!v.hp && hpCur === ''} onChange={(e) => setNl(e.target.value)} /></label>
        : v.hp?.nonlethal ? <span data-testid="nonlethal-count">Nonlethal {v.hp.nonlethal}</span> : null}
      {systemRules.damage?.temporary && (full
        ? <label className="flex items-center gap-1">Temporary <input aria-label="Temporary hit points" type="number" min={0} className={num} placeholder="0" value={tempHp} disabled={!v.hp && hpCur === ''} onChange={(e) => setTempHp(e.target.value)} /></label>
        : v.hp?.temp ? <span data-testid="temp-hp">Temporary {v.hp.temp}</span> : null)}
      {systemRules.damage?.stops && full && (
        <label className="flex w-full items-center gap-1" title="Over the stat block's, if it has any. For example: DR 5/magic; fire 10; immune cold; vulnerable fire">Damage defences
          <input aria-label="Damage defences" maxLength={200} className="min-w-0 flex-1 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="DR 5/magic; fire 10" value={stopsText} onChange={(e) => setStopsText(e.target.value)} />
        </label>
      )}
      {nonlethalStatus(v.hp) && <span className="font-semibold text-amber-300" data-testid="nonlethal-status">{NONLETHAL_WORDS[nonlethalStatus(v.hp)!]}</span>}
      {full && <label className="flex items-center gap-1">{w.defence} <input aria-label={w.defence} type="number" className={num} value={ac} onChange={(e) => setAc(e.target.value)} /></label>}
      {full && otherDefences.map((d) => (
        <label key={d.key} className="flex items-center gap-1" data-testid={`defence-${d.key}`} title={`Kept from the ${d.label} typed at an attack, or set here; empty, the next attack against it asks. A linked stat block's own wins.`}>
          {d.label} <input aria-label={d.label} type="number" step={1} className={num} value={defs[d.key] ?? ''} onChange={(e) => setDefs((was) => ({ ...was, [d.key]: e.target.value }))} />
        </label>
      ))}
      <label className="flex items-center gap-1">
        {full ? <input aria-label="Slot 1 label" className="w-20 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="Slot 1" value={s1l} onChange={(e) => setS1l(e.target.value)} /> : <span>{v.slot1?.label ?? 'Slot 1'}</span>}
        <input aria-label="Slot 1 value" type="number" className={num} value={s1v} disabled={!full && !v.slot1} onChange={(e) => setS1v(e.target.value)} />
        {full ? <><span>/</span><input aria-label="Slot 1 maximum" type="number" title="With a maximum the slot is a bar; without one, a chip" className={num} value={s1m} onChange={(e) => setS1m(e.target.value)} /></> : v.slot1?.max !== undefined && <span>/ {v.slot1.max}</span>}
      </label>
      <label className="flex items-center gap-1">
        {full ? <input aria-label="Slot 2 label" className="w-20 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="Slot 2" value={s2l} onChange={(e) => setS2l(e.target.value)} /> : <span>{v.slot2?.label ?? 'Slot 2'}</span>}
        <input aria-label="Slot 2 value" type="number" className={num} value={s2v} disabled={!full && !v.slot2} onChange={(e) => setS2v(e.target.value)} />
        {full ? <><span>/</span><input aria-label="Slot 2 maximum" type="number" title="With a maximum the slot is a bar; without one, a chip" className={num} value={s2m} onChange={(e) => setS2m(e.target.value)} /></> : v.slot2?.max !== undefined && <span>/ {v.slot2.max}</span>}
      </label>
      {full && (
        <>
          <label className="flex items-center gap-1">{w.initiative} <input aria-label="Initiative bonus" type="number" className={num} value={ib} onChange={(e) => setIb(e.target.value)} /></label>
          <label className="flex items-center gap-1">rolled <input aria-label="Initiative roll" type="number" className={num} value={ir} onChange={(e) => setIr(e.target.value)} /></label>
          <label className="flex items-center gap-1" title="Its speed for the reachable area (qa-runs#1161); empty, its sheet's">Speed <input aria-label="Speed" type="number" min={0} className={num} value={spd} onChange={(e) => setSpd(e.target.value)} /></label>
          <label className="flex items-center gap-1" title="Its fly speed, used while it is flying (qa-runs#1161); empty, its sheet's">Fly <input aria-label="Fly speed" type="number" min={0} className={num} value={fly} onChange={(e) => setFly(e.target.value)} /></label>
          <label className="flex items-center gap-1" title="Heavy armor runs three times its speed, not four">
            <input type="checkbox" aria-label="Heavy armor" checked={v.heavy === true} onChange={(e) => act({ kind: 'object.values', id: o.id, values: { heavy: e.target.checked } as never })} /> Heavy armor
          </label>
          <label className="flex items-center gap-1">Conditions <input aria-label="Conditions" className="w-36 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="prone, shaken" value={cond} onChange={(e) => setCond(e.target.value)} /></label>
          {countsLevels && (v.conditions ?? []).map((c) => <label key={c} className="flex items-center gap-1">{c} level <input aria-label={`${c} level`} className="w-10 rounded bg-stone-800 px-1 py-0.5 text-xs" inputMode="numeric" value={levels[conditionLevelKey(c)] ?? ''} onChange={(e) => { const k = conditionLevelKey(c); setLevels((l) => ({ ...l, [k]: e.target.value })); }} /></label>)}
          <span className="flex items-center gap-1">
            <input aria-label="Add a condition" list={suggestions.length ? listId : undefined} className="w-28 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder={suggestions.length ? 'pick or type' : 'type one'} value={adding}
              onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCondition(); } }} />
            {suggestions.length > 0 && <datalist id={listId} data-testid="condition-suggestions">{suggestions.map((n) => <option key={n} value={n} />)}</datalist>}
            <button type="button" className={btn} disabled={!adding.trim()} onClick={addCondition}>Add</button>
          </span>
        </>
      )}
      <button type="submit" className={btn}>Save numbers</button>
    </form>
  );
}

/**
 * A token's resources in its system's words (issue 1312): one row per resource the system declares, its current number
 * and, where the system gives it one, its maximum. The DM sets any of them; an owner, the current numbers of the ones
 * the token carries (as with hit points), so their form leaves the rest alone. One `object.values` with the whole record.
 */
export function ResourcesEditor({ o, full }: { o: { id: string; values: Values }; full: boolean }) {
  const { act, systemRules } = useTableState();
  const rules = systemRules.resources;
  const have = o.values.resources;
  // Its condition monitor (issue 1526): the levels its numbers reach, their modifiers, and — the DM's — a full last box.
  const wounds = woundsOf(useWoundRules(), have);
  const [text, setText] = useState<Record<string, { current: string; max: string }>>({});
  useEffect(() => {
    setText(Object.fromEntries(rules.map((r) => [r.key, { current: have?.[r.key] ? String(have[r.key]!.current) : '', max: have?.[r.key]?.max !== undefined ? String(have[r.key]!.max) : '' }])));
  }, [o.id, have, rules]);
  if (!rules.length) return null;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, { current: number; max?: number }> = { ...(have ?? {}) };
    for (const r of rules) {
      const cur = numOrNull(text[r.key]?.current ?? '');
      if (!full) { if (next[r.key] && cur !== null) next[r.key] = { ...next[r.key]!, current: cur }; continue; }
      const max = r.max ? numOrNull(text[r.key]?.max ?? '') : null;
      if (cur === null) delete next[r.key];
      else next[r.key] = max !== null && max > 0 ? { current: cur, max } : { current: cur };
    }
    const value = Object.keys(next).length ? next : null;
    if (JSON.stringify(value) !== JSON.stringify(have ?? null)) act({ kind: 'object.values', id: o.id, values: { resources: value } });
  };
  const set = (key: string, part: 'current' | 'max', v: string) => setText((t) => ({ ...t, [key]: { current: t[key]?.current ?? '', max: t[key]?.max ?? '', [part]: v } }));
  return (
    <div className="space-y-1">
    <form className="flex flex-wrap items-center gap-2 text-xs" data-testid="resources-editor" onSubmit={submit}>
      {rules.map((r) => (
        <label key={r.key} className="flex items-center gap-1">{r.label}
          <input aria-label={`Current ${r.label}`} type="number" className={num} value={text[r.key]?.current ?? ''} disabled={!full && !have?.[r.key]} onChange={(e) => set(r.key, 'current', e.target.value)} />
          {r.max && <><span>/</span>{full
            ? <input aria-label={`Maximum ${r.label}`} type="number" className={num} value={text[r.key]?.max ?? ''} onChange={(e) => set(r.key, 'max', e.target.value)} />
            : <span>{have?.[r.key]?.max ?? '—'}</span>}</>}
        </label>
      ))}
      <button type="submit" className={btn}>Save {rules.length === 1 ? rules[0]!.label : 'resources'}</button>
    </form>
    {(wounds.words.length > 0 || wounds.target !== 0 || wounds.initiative !== 0) && (
      <p className="text-xs" data-testid="wound-line">
        <span className="font-semibold text-amber-300" data-testid="wound-words">{wounds.words.join(', ')}</span>
        {(wounds.target !== 0 || wounds.initiative !== 0) && <span className="ml-2 text-stone-300" data-testid="wound-mods">{woundMods(wounds.target, wounds.initiative)}</span>}
      </p>
    )}
    {full && wounds.full.map((l) => <p key={l} className="text-xs font-semibold text-red-300" data-testid="dead-past">{DEAD_PAST(l)}</p>)}
    {full && <DamageControl id={o.id} />}
    </div>
  );
}

/** A wound's modifiers in words (issue 1526): "+2 TN on tests, −2 Init". */
export const woundMods = (target: number, initiative: number): string =>
  [target ? `${target > 0 ? '+' : '−'}${Math.abs(target)} TN on tests` : '', initiative ? `${initiative > 0 ? '+' : '−'}${Math.abs(initiative)} Init` : ''].filter(Boolean).join(', ');

/**
 * The DM's Damage control (issue 1526; docs/MRCS/shadowrun-3.md §5.4): an amount and, on a system that names kinds of
 * damage, the kind, marked on the token's condition monitor by the server down that kind's chain — what one resource
 * cannot take passing to the next. Healing is the numbers above, typed.
 */
/** The Damage box on a system with no monitors, where what stops damage still applies: it marks the hit points. */
function StopsDamage({ id }: { id: string }) {
  const { systemRules } = useTableState();
  return systemRules.damage?.stops && !systemRules.resources?.length ? <DamageControl id={id} /> : null;
}

export function DamageControl({ id }: { id: string }) {
  const { act, setNotice, systemRules } = useTableState();
  const kinds = systemRules.damageKinds ?? [];
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState(kinds[0]?.key ?? '');
  const types = systemRules.damage?.stops ? systemRules.damage.types : [];
  const [type, setType] = useState('');
  const n = Math.trunc(Number(amount));
  const ok = amount.trim() !== '' && Number.isFinite(n) && n >= 1 && n <= 1000;
  return (
    <form className="flex flex-wrap items-center gap-1 text-xs" data-testid="damage-control" onSubmit={(e) => {
      e.preventDefault();
      if (!ok) return;
      const k = kinds.find((x) => x.key === kind)?.key ?? kinds[0]?.key;
      act({ kind: 'object.hurt', id, amount: n, ...(k ? { damageKind: k } : {}), ...(type ? { type } : {}) } as never, (reply) => { if (reply.ok) setAmount(''); else setNotice(reply.reason ?? 'That damage could not be marked.'); });
    }}>
      <label className="flex items-center gap-1">Damage <input aria-label="Damage boxes" type="number" min={1} max={1000} className={num} value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
      {kinds.length > 1 && (
        <select aria-label="Damage kind" className="rounded bg-stone-800 px-1 py-0.5 text-xs" value={kind} onChange={(e) => setKind(e.target.value)}>
          {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
      )}
      {types.length > 0 && (
        <select aria-label="Damage type" className="rounded bg-stone-800 px-1 py-0.5 text-xs" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">No type</option>
          {types.map((t) => <option key={t.key} value={t.label.toLowerCase()}>{t.label}</option>)}
        </select>
      )}
      <button type="submit" className={btn} disabled={!ok}>Mark damage</button>
    </form>
  );
}

/**
 * Add tokens to the encounter from the board (design/20 §3.2): each becomes
 * a combatant named after its token, its bonus seeded from the token's
 * numbers; one action for a whole selection, so six goblins are one click.
 */
export function addToEncounter(act: (body: EventBody) => void, objects: Array<{ id: string; name: string; values: { initBonus?: number } }>): void {
  if (objects.length === 0) return;
  act({ kind: 'encounter.add', combatants: objects.map((o) => ({ id: crypto.randomUUID(), name: o.name, initBonus: o.values.initBonus ?? 0, tokens: [o.id], shown: false })) });
}

/** The combatant a token acts for, named in the panel (design/20 §3.4): never painted on the board. */
export function actsAsLine(enc: Encounter | PlayerEncounter | undefined, id: string): string {
  const c = enc ? enc.combatants.find((x) => x.tokens.includes(id)) : undefined;
  if (!c) return enc?.running ? 'Not in this fight.' : '';
  return `Acts as ${c.name} in the turn order${enc!.running && 'active' in enc! ? ((enc as PlayerEncounter).active === c.id ? ' — acting now' : '') : ''}.`;
}

/** "Shaken · 2 rounds": a turn-order effect's label and how long it has left, in the system's words. */
export function effectChip(e: Effect): string {
  if (!e.duration) return e.label;
  const n = e.duration.amount;
  return `${e.label} · ${n} ${n === 1 ? e.duration.unit.label : e.duration.unit.plural}`;
}

/** A rulebook's slug in words, for the identity header's source: "monster-manual-v35" is "Monster Manual". */
export const bookWords = (slug: string): string => slug.replace(/-v\d+$/, '').split('-').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** The kind of thing in words, for the identity header's line (issue 1575). */
const kindWord = (o: { kind: string; pc?: boolean; layer?: string }): string =>
  (o.kind === 'prop' ? 'Prop' : o.pc ? 'Player character' : o.layer === 'scenery' ? 'Scenery' : 'Creature');

/** A sense in the system's distance where it has one: "darkvision 60 ft.", "low-light vision". */
export function senseWords(v: Sense, label: string, distance: { unit: string; perSquare: number } | null): string {
  if (v.range === null) return label.toLowerCase();
  const n = distance ? Math.round(v.range * distance.perSquare * 100) / 100 : v.range;
  return `${label.toLowerCase()} ${n}${distance ? ` ${distance.unit}` : ' sq'}`;
}

/** The Light & vision section's one line: "darkvision 60 ft. · no light", "torch 4/8". */
export function lightVisionSummary(o: { light?: Light; vision?: Sense[]; auras?: unknown[] }, kinds: ReadonlyArray<{ key: string; label: string }>, distance: { unit: string; perSquare: number } | null): string {
  const seen = (o.vision ?? []).map((v) => senseWords(v, kinds.find((k) => k.key === v.key)?.label ?? v.key.replace(/_/g, ' '), distance));
  const light = o.light ? (o.light.on ? `light ${o.light.bright}/${o.light.dim}` : 'light off') : 'no light';
  const auras = o.auras?.length ? [`${o.auras.length} aura${o.auras.length === 1 ? '' : 's'}`] : [];
  return [...seen, light, ...auras].join(' · ');
}

/** The Players see section's one line (issue 1575): what Properties tells them, what was revealed, or "nothing". */
export function playersSeeSummary(o: { told?: Field[]; facts?: unknown[]; infer?: true }, labels: Record<Field, string>): string {
  const parts = [...(o.told ?? []).map((f) => labels[f])];
  if (o.facts?.length) parts.push(`${o.facts.length} fact${o.facts.length === 1 ? '' : 's'}`);
  if (o.infer) parts.push('they work out its numbers');
  return parts.length ? parts.join(', ') : 'nothing';
}

/**
 * What Properties tells the other players of a token (issue 1575, decision 5): per field, separate from the board's
 * shares. Nothing is told by default; a told field reaches them under "What you know", its value sent with it.
 */
function TellEditor({ id, told, fields }: { id: string; told: readonly Field[]; fields: readonly Field[] }) {
  const { act } = useTableState();
  const FIELD_LABELS = fieldLabels(useNumberWords());
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="tells">
      <span className="text-stone-400">In Properties, they are told:</span>
      {fields.map((f) => (
        <label key={f} className="flex items-center gap-1">
          <input type="checkbox" aria-label={`Tell the players its ${FIELD_LABELS[f]}`} checked={told.includes(f)} onChange={(e) => act({ kind: 'object.tell', id, tells: { [f]: e.target.checked } })} />
          {FIELD_LABELS[f]}
        </label>
      ))}
    </div>
  );
}

/** What a player who does not own it is told of a token (issue 1575): the told fields, its revealed facts, what was worked out. */
export function knownLines(o: { name?: string; told?: Field[]; values: Values; facts?: Fact[]; disposition?: Disposition }, w: NumberWords, resources: ReadonlyArray<{ key: string; label: string; levels?: WoundLevel[]; end?: true }>, health: string | null): string[] {
  const told = new Set(o.told ?? []);
  const values: Values = {};
  for (const f of told) if (f !== 'name' && f !== 'side' && f in o.values) (values as Record<string, unknown>)[f] = (o.values as Record<string, unknown>)[f];
  const line = valuesLine(values, w, told.has('resources') ? resources : [], health);
  // Its side, told (qa-runs#1157): as it reads now, sent with the telling.
  const side = told.has('side') && o.disposition ? [`Side: ${SIDE_WORDS[o.disposition]}`] : [];
  return [...(line ? line.split(' · ') : []), ...side];
}

/** The identity header (issue 1575; issue 1541's look): the portrait, the name in Cinzel, a kind line, the source, a who line. */
function IdentityHeader({ name, asset, line, lineTestId, source, owners, children, tint }: {
  name: ReactNode; asset: string | null | undefined; line: string; lineTestId?: string; source?: ReactNode; owners?: ReactNode; children?: ReactNode; portraitName?: string;
  /** Colour-match (qa-runs#362): the selected token's marker colour, tinting the header and its rule. */
  tint?: string;
}) {
  return (
    <div className={`flex items-center gap-3 border-b border-vtt-line-soft pb-3 ${tint ? 'rounded-t px-1 pt-1' : ''}`} data-testid="identity" data-tint={tint}
      style={tint ? { borderBottomColor: tint, borderBottomWidth: 2, backgroundColor: `color-mix(in srgb, ${tint} 22%, transparent)` } : undefined}>
      {children}
      <div className="min-w-0 flex-1">
        {name}
        <p className="mt-0.5 text-[0.71875rem] text-vtt-muted" data-testid={lineTestId}>{line}</p>
        {source}
        {owners}
      </div>
    </div>
  );
}

/** One selected thing, for the DM: everything about it, in sections (issue 1575). */
function ThingEditor({ o, reveal, extent, tint }: { o: DmObject; reveal: (ids: string[]) => void; extent: { width: number; height: number }; tint?: string }) {
  const { act, meId, store, systemRules } = useTableState();
  const words = useNumberWords();
  const shareFields = useShareFields(systemRules.resources.length > 0, true);
  const { table } = useShell();
  const health = useHealthResource();
  const target = useTarget();
  const enc = (store.state.encounter ?? emptyEncounter()) as Encounter;
  const combatant = combatantOf(enc, o.id);
  const kind = panelKindOf(o);
  const token = o.kind === 'token';
  const [name, setName] = useState(o.name ?? '');
  const [w, setW] = useState(String(o.size.w));
  const [h, setH] = useState(String(o.size.h));
  const [copies, setCopies] = useState('5');
  const [more, setMore] = useState(false);
  const colourMatch = usePanelPrefs().colourMatch === true;
  useEffect(() => { setName(o.name ?? ''); setW(String(o.size.w)); setH(String(o.size.h)); setMore(false); }, [o.id, o.name, o.size.w, o.size.h]);
  const preset = SIZES.find((s) => s.size.w === o.size.w && s.size.h === o.size.h);
  const resize = (size: Size, fit: ArtFit = o.fit) => act({ kind: 'object.resize', id: o.id, size, fit });
  const rename = () => { if (name.trim() && name.trim() !== o.name) act({ kind: 'object.rename', id: o.id, name: name.trim(), rev: o.rev }); else setName(o.name ?? ''); };
  const ref = o.entry && o.entry.segment && o.entry.slug && o.entry.system ? o.entry : null;
  const block = useStatBlock(token ? ref : null, o.id);
  const found = block && block !== 'gone' ? block : null;
  const at = squareWords(squareOf(o, store.state.grid, extent));
  const v = o.values;
  const bar = healthOf(v, health, words);
  // The tiles (issue 1541): the main defence, the system's next two (by its data's order) from the
  // linked stat block where it carries them, and the initiative bonus.
  const others = (systemRules.attack?.defences ?? []).filter((d) => d.key !== systemRules.attack?.main).slice(0, 2);
  // A defence's label shortened to its first word, capitalised, as the mock-up's tiles read.
  const shortLabel = (l: string) => (l.split(/[\s-]+/)[0] ?? l).replace(/^\w/, (c) => c.toUpperCase());
  // Issue 1584: a marker (a trap) shows its checks instead — the DC to find it and to disable it, under its family's
  // labels ("Search DC" / "Perception DC", "Disable Device DC"); the DM's alone, as the stat block they come with is.
  const checks = found?.checks ?? [];
  const tiles = token && checks.length ? checks.map((c) => ({ label: c.label, value: String(c.value) })) : token ? [
    { label: words.defence, value: v.ac !== undefined ? String(v.ac) : null },
    ...others.map((d) => ({ label: shortLabel(d.label), value: found?.stats.find((r) => r.key?.startsWith(d.key))?.value.match(/^[+\-−]?\d+/)?.[0] ?? (v.defences?.[d.key] !== undefined ? String(v.defences[d.key]) : null) })),
    { label: words.initiative, value: v.initBonus !== undefined ? `${v.initBonus >= 0 ? '+' : ''}${v.initBonus}` : null },
  ] : [];
  const chips = [...(v.conditions ?? []), ...(combatant?.effects ?? []).map(effectChip)];
  const acting = enc.running && combatant && enc.combatants[enc.activeIndex]?.id === combatant.id;
  const FIELD_LABELS = fieldLabels(words);
  const vitalsSummary = [bar ? `${bar.label} ${bar.current}/${bar.max}` : '', chips.length ? `${chips.length} condition${chips.length === 1 ? '' : 's'}` : '', o.dead ? 'dead' : '', acting ? 'acting now' : ''].filter(Boolean).join(' · ');
  const aimed = target && target !== o.id ? store.state.objects[target] : undefined;
  const advanced = [o.follows ? 'follows' : '', o.noAoo ? 'no attacks of opportunity' : '', o.astral ? `astral: ${o.astral}` : '', (o as { jacked?: true }).jacked ? 'jacked in' : '', o.matrix ? 'a host' : ''].filter(Boolean).join(' · ');
  return (
    <section className="space-y-2" data-testid="thing-editor" aria-label={`Selected: ${o.name}`}>
      <IdentityHeader
        name={(
          // Rename in place (issue 1575): the name is its own field, saved on Enter or on leaving it.
          <form onSubmit={(e) => { e.preventDefault(); rename(); }}>
            <input aria-label="Name" className={`w-full min-w-0 rounded border border-transparent bg-transparent px-0.5 font-display ${(name ?? '').length > 14 ? 'text-sm' : 'text-base'} font-semibold text-vtt-fg hover:border-vtt-line focus:border-vtt-gold-dim focus:bg-stone-800`}
              value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onBlur={rename} title="Rename: type a new name, then Enter." />
          </form>
        )}
        asset={o.asset}
        line={`${kindWord(o)} · ${o.size.w}×${o.size.h} · square ${at}`}
        lineTestId="thing-kind"
        // The compendium source, by its own name whatever the token is called (qa-runs#362): "from: Goblin".
        source={ref ? <a className="block truncate text-[0.71875rem] text-vtt-gold underline decoration-vtt-gold-dim" href={entryPageUrl(ref)} target="_blank" rel="noopener noreferrer" data-testid="identity-source">from: {ref.name}{ref.book ? ` — ${bookWords(ref.book)}` : ''}</a> : undefined}
        tint={tint}
        owners={<p className="text-[0.71875rem] text-stone-400"><span data-testid="owners-line">{ownersLine(o, table.members, meId, store.state.objects)}</span> <span data-testid="thing-state">{o.hidden ? 'Hidden' : 'Shown'}{o.locked ? ' · locked' : ''}{o.asset ? ' · pictured' : ''}</span></p>}
      ><Portrait name={o.name} asset={o.asset} /></IdentityHeader>

      {/* The quick toggles (issue 1575): what a DM flips most, above every section. */}
      <div className="flex flex-wrap items-center gap-1" data-testid="quick-toggles">
        {/* Colour-match (qa-runs#362): the DM's own look, remembered with their layout; the panel takes the token's marker colour. */}
        {token && (
          <label className="flex items-center gap-1 text-xs" title="Tint this panel with the selected token's marker colour, with a matching swatch in the object list and the turn order.">
            <input type="checkbox" aria-label="Colour-match" checked={colourMatch} onChange={(e) => panelPrefs.setColourMatch(e.target.checked)} /> Colour-match
          </label>
        )}
        <button type="button" className={btn} aria-label={`${o.hidden ? 'Reveal' : 'Hide'} ${o.name}`} title={o.hidden ? 'Hidden from the players: press to show it.' : 'Shown to the players: press to hide it.'}
          onClick={() => (o.hidden ? reveal([o.id]) : act({ kind: 'object.hide', ids: [o.id] }))}>{o.hidden ? 'Hidden' : 'Shown'}</button>
        <button type="button" className={btn} aria-pressed={o.locked} aria-label={`${o.locked ? 'Unlock' : 'Lock'} ${o.name}`} onClick={() => act({ kind: 'object.lock', id: o.id, locked: !o.locked })}>
          {o.locked ? 'Locked' : 'Unlocked'}
        </button>
        {token && (combatant
          ? <button type="button" className={btn} aria-pressed aria-label={`Remove ${o.name} from the encounter`} title="In the encounter. Press to take it out; the combatant stays in the tracker, tokenless." onClick={() => act({ kind: 'encounter.link', id: combatant.id, tokens: combatant.tokens.filter((t) => t !== o.id) })}>In encounter</button>
          : <button type="button" className={btn} aria-pressed={false} aria-label={`Add ${o.name} to the encounter`} onClick={() => addToEncounter(act, [o])}>Not in encounter</button>)}
        <button type="button" className={btn} aria-expanded={more} aria-label="More" title="More: ownership, player character, nameplate, delete." onClick={() => setMore((m) => !m)}>⋯</button>
      </div>
      {more && (
        <div className="vtt-popover flex flex-wrap items-center gap-2 p-2 text-xs" data-testid="quick-more">
          <button type="button" className={btn} onClick={() => { act({ kind: 'object.assign', id: o.id, owners: [meId] }); setMore(false); }}>Make it mine alone</button>
          <label className="flex items-center gap-1"><input type="checkbox" aria-label="Is a player character" checked={o.pc} onChange={(e) => act({ kind: 'object.pc', id: o.id, pc: e.target.checked })} /> player character</label>
          {token && <label className="flex items-center gap-1"><input type="checkbox" aria-label="Shows its nameplate" checked={o.nameplate} onChange={(e) => act({ kind: 'object.nameplate', id: o.id, shown: e.target.checked })} /> nameplate</label>}
          <button type="button" className={`${btn} border-red-800 text-red-300`} onClick={() => { setMore(false); boardBus.requestDelete([o.id]); }}>Delete…</button>
        </div>
      )}

      {/* Match to a creature or a table character (issue 1612, Tom's A): at the top, the DM's alone. */}
      {token && (
        <div className="space-y-1" data-testid="match-to">
          <TokenEntry o={o} kind={kind} mode="match" />
          <TokenCharacter o={o} mode="match" />
        </div>
      )}
      {token && (
        <Section kind={kind} id="vitals" title="Vitals" summary={vitalsSummary} open>
          {bar && <HpBar current={bar.current} max={bar.max} label={bar.label} />}
          <StatTiles tiles={tiles} />
          <ConditionChips chips={chips} onAdd={() => document.querySelector<HTMLInputElement>(`[data-testid="section-vitals"] input[aria-label="Add a condition"]`)?.focus()} />
          {/* What a DM changes mid-fight first (the 2026-10-15 session pass): hit points, conditions and dead. */}
          <ValuesEditor o={o} full />
          <StopsDamage id={o.id} />
          <DeadToggle o={o} />
          {/* A system whose health is one of its resources (a condition monitor, issue 1526): they are its vitals. */}
          {health && <ResourcesEditor o={o} full />}
        </Section>
      )}
      {token && (
        <Section kind={kind} id="statblock" title="Stat block" summary={ref ? (found ? `${revealedCount(found.stats, o.facts)} of ${found.stats.length} revealed` : ref.name) : 'not linked'} open={ref !== null}>
          <TokenEntry o={o} kind={kind} mode="block" />
        </Section>
      )}
      {token && (
        <Section kind={kind} id="character" title="Character" summary={o.character ? 'stands for a character' : 'none'} open={o.pc}>
          <TokenCharacter o={o} mode="block" />
        </Section>
      )}
      {token && systemRules.attack && (
        <Section kind={kind} id="actions" title="Actions" summary={aimed ? `at ${tokenLabel(aimed, store.state.encounter) || 'the target'}` : 'no target'} open={enc.running}>
          <TokenActions o={o} />
        </Section>
      )}
      {token && (
        <Section kind={kind} id="turn" title="Turn order" summary={combatant ? `${combatant.initRoll !== undefined && combatant.initRoll !== null ? `init ${combatant.initRoll}` : 'in the order'}${acting ? ' · acting now' : ''}` : enc.running ? 'not in this fight' : 'not in the order'} open={false}>
          <p className="flex flex-wrap items-center gap-2 text-xs text-stone-400" data-testid="acts-as">
            <span>{combatant ? `Acts as ${combatant.name} in the turn order${acting ? ' — acting now' : ''}.` : enc.running ? 'Not in this fight.' : 'Not in the turn order.'}</span>
            {combatant
              ? <button type="button" className={btn} aria-label={`Unlink ${o.name} from its combatant`} title="The combatant stays in the tracker, tokenless." onClick={() => act({ kind: 'encounter.link', id: combatant.id, tokens: combatant.tokens.filter((t) => t !== o.id) })}>Unlink</button>
              : <button type="button" className={btn} aria-label={`Add ${o.name} to the turn order`} onClick={() => addToEncounter(act, [o])}>Add to encounter</button>}
          </p>
        </Section>
      )}
      <Section kind={kind} id="token" title="Token" summary={`${o.size.w}×${o.size.h} · ${o.layer === 'dm' ? 'DM' : o.layer} layer`} open={!token}>
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-stone-400">Size</span>
          {SIZES.map((s) => (
            <button key={s.label} type="button" className={btn} aria-pressed={preset?.label === s.label} onClick={() => resize(s.size)}>{s.label}</button>
          ))}
          <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const sw = Number(w); const sh = Number(h); if (sw > 0 && sh > 0 && sw <= 50 && sh <= 50) resize({ w: sw, h: sh }); }}>
            <input aria-label="Squares across" type="number" step="any" min={0.1} max={50} className={num} value={w} onChange={(e) => setW(e.target.value)} />
            <span className="text-xs text-stone-500">×</span>
            <input aria-label="Squares down" type="number" step="any" min={0.1} max={50} className={num} value={h} onChange={(e) => setH(e.target.value)} />
            <button type="submit" className={btn}>Free size</button>
          </form>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1">Art
            <select aria-label="Art fit" className="rounded bg-stone-800 px-1 py-0.5" value={o.fit} onChange={(e) => resize(o.size, e.target.value as ArtFit)}>
              <option value="stretch">stretches to its squares</option>
              <option value="inside">sits inside its squares</option>
            </select>
          </label>
          <label className="flex items-center gap-1">Layer
            <select aria-label="Layer" className="rounded bg-stone-800 px-1 py-0.5" value={o.layer} onChange={(e) => act({ kind: 'object.layer', id: o.id, layer: e.target.value as Layer })}>
              <option value="scenery">scenery</option>
              <option value="token">token</option>
              <option value="dm">DM</option>
            </select>
          </label>
          {token && (
            <label className="flex items-center gap-1" title="The name under the token on the board. Hidden, nothing renders — not a placeholder.">
              <input type="checkbox" aria-label="Nameplate" checked={o.nameplate} onChange={(e) => act({ kind: 'object.nameplate', id: o.id, shown: e.target.checked })} /> nameplate
            </label>
          )}
          <label className="flex items-center gap-1" title="A player character's numbers are hidden from the other players until its owner shares them.">
            <input type="checkbox" aria-label="Player character" checked={o.pc} onChange={(e) => act({ kind: 'object.pc', id: o.id, pc: e.target.checked })} /> player character
          </label>
        </div>
        {token && <RotateControl o={o} />}
        {token && <HeightControl o={o} />}
        {token && <div className="flex flex-wrap items-center gap-3"><MarkerSwatches o={o} /><DispositionPicker o={o} /></div>}
        <div className="flex flex-wrap gap-1" data-testid="owners">
          <button type="button" className={btn} aria-label={`Give ${o.name} to me`} onClick={() => act({ kind: 'object.assign', id: o.id, owners: [meId] })}>Assign to me</button>
          {table.members.filter((m) => m.role === 'player').map((m) => {
            // The person, not whichever character they have (qa-runs 1261): this is who gets the token.
            const who = m.handle ? `@${m.handle}` : m.displayName?.trim() || 'a player';
            return (
              <button key={m.id} type="button" className={btn} aria-pressed={o.owners.includes(m.id)} aria-label={`${o.owners.includes(m.id) ? 'Take from' : 'Give to'} ${who}`}
                onClick={() => act({ kind: 'object.assign', id: o.id, owners: o.owners.includes(m.id) ? o.owners.filter((x) => x !== m.id) : [...o.owners, m.id] })}>
                {o.owners.includes(m.id) ? `Owned by ${who}` : `Give to ${who}`}
              </button>
            );
          })}
        </div>
      </Section>
      {token && (
        <Section kind={kind} id="light" title="Light & vision" summary={lightVisionSummary(o, systemRules.vision, systemRules.distance)} open={false}>
          <LightControl o={o} />
          <VisionControl o={o} />
          <AurasControl o={o} />
        </Section>
      )}
      {token && (
        <Section kind={kind} id="players" title="Players see" summary={playersSeeSummary(o, FIELD_LABELS)} open={false}>
          <TellEditor id={o.id} told={o.told ?? []} fields={shareFields} />
          <InferToggle o={o} />
          <div className="space-y-1 border-t border-vtt-line-soft pt-2">
            <p className="text-[0.6875rem] text-stone-400">On the board and in the turn order (nameplates, overlays, the stones):</p>
            <SharesEditor id={o.id} shares={o.shares} fields={shareFields} />
          </div>
        </Section>
      )}
      {token && (
        <Section kind={kind} id="advanced" title="Advanced" summary={advanced || undefined} open={false}>
          <FollowPicker o={o} />
          {!health && <ResourcesEditor o={o} full />}
          <NoAooToggle o={o} />
          <NoExtraToggle o={o} />
          <AstralControl o={o} />
          <HostEditor o={o} />
          <CyberPanel o={o as never} isDm />
        </Section>
      )}
      <form className="flex flex-wrap items-center gap-1 pt-1" onSubmit={(e) => {
        e.preventDefault();
        const n = Math.max(1, Math.min(50, Math.floor(Number(copies) || 0)));
        act({ kind: 'object.duplicate', id: o.id, ids: Array.from({ length: n }, () => crypto.randomUUID()) });
      }}>
        <input aria-label="Copies" type="number" min={1} max={50} className={num} value={copies} onChange={(e) => setCopies(e.target.value)} />
        <button type="submit" className={btn} aria-label={`Duplicate ${o.name}`}>Duplicate</button>
        <button type="button" className={`${btn} ml-auto border-red-800 text-red-300 hover:bg-red-950`} aria-label={`Remove ${o.name}`} data-testid="delete-thing" onClick={() => boardBus.requestDelete([o.id])}>Delete</button>
      </form>
    </section>
  );
}

/**
 * Someone else's token, for a player (issue 1575, decision 5): nothing about it by default — "Unknown creature", its
 * square, and "You don't know anything about this yet." Once the DM tells a field or reveals a fact, exactly that, under
 * "What you know"; nothing else is shown, counted or hinted. Only what this viewer was sent is read, and of it only what
 * was told: the board's own shares (its nameplate, its stone) are not this panel's to repeat.
 */
function UnknownThing({ o, extent }: { o: PlayerObject; extent: { width: number; height: number } }) {
  const { store, systemRules } = useTableState();
  const words = useNumberWords();
  const told = new Set(o.told ?? []);
  const named = told.has('name') && o.name ? o.name : null;
  const lines = knownLines(o, words, systemRules.resources, systemRules.health);
  const facts = o.facts ?? [];
  const worked = useInferredLines(o);
  const knows = named !== null || lines.length > 0 || facts.length > 0 || worked.length > 0;
  return (
    <section className="space-y-2" data-testid="thing-view">
      <IdentityHeader
        name={<p className="font-display text-base font-semibold text-vtt-fg" data-testid="unknown-name">{named ?? 'Unknown creature'}</p>}
        asset={null}
        line={`at ${squareWords(squareOf(o, store.state.grid, extent))}`}
        lineTestId="thing-at"
      ><Portrait name={named ?? undefined} asset={null} /></IdentityHeader>
      {!knows && <p className="text-sm text-stone-400" data-testid="unknown-creature">You don't know anything about this yet.</p>}
      {knows && (
        <div className="vtt-card space-y-1 px-2.5 py-2 text-xs" data-testid="known-facts">
          <p className="vtt-section-head">What you know</p>
          <ul className="space-y-0.5 text-stone-200">
            {named && told.has('name') && o.entry && o.entry.name !== named ? <li>{o.entry.name}</li> : null}
            {lines.map((l) => <li key={l} data-testid="told-line">{l}</li>)}
            {facts.map((f) => <li key={f.label} className="break-words"><span className="text-stone-500">{f.label}:</span> {f.value}</li>)}
            {worked.map((l) => <li key={l} className="break-words" data-testid="inferred-line">{l}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

/** A player's own token (issue 1575): the DM's sections where they have rights, their own numbers whole. */
function OwnThing({ o, extent }: { o: PlayerObject; extent: { width: number; height: number } }) {
  const { store, meId, systemRules } = useTableState();
  const { table } = useShell();
  const words = useNumberWords();
  const health = useHealthResource();
  const mayAct = useMayAct();
  const shareFields = useShareFields(systemRules.resources.length > 0, false);
  const enc = store.state.encounter as Encounter | PlayerEncounter | undefined;
  const kind = panelKindOf(o);
  const v = o.values;
  const bar = healthOf(v, health, words);
  const effects = enc ? enc.combatants.find((c) => c.tokens.includes(o.id))?.effects ?? [] : [];
  const chips = [...(v.conditions ?? []), ...effects.map(effectChip)];
  const tiles = o.kind === 'token' ? [
    { label: words.defence, value: v.ac !== undefined ? String(v.ac) : null },
    { label: words.initiative, value: v.initBonus !== undefined ? `${v.initBonus >= 0 ? '+' : ''}${v.initBonus}` : null },
  ] : [];
  const token = o.kind === 'token';
  const FIELD_LABELS = fieldLabels(words);
  const shared = o.shares ? FIELDS.filter((f) => shareFields.includes(f) && (o.shares![f] || o.told?.includes(f))).map((f) => FIELD_LABELS[f]) : [];
  return (
    <section className="space-y-2" data-testid="thing-view">
      <IdentityHeader
        name={<p className="font-display text-base font-semibold text-vtt-fg">{o.name ?? <span className="italic text-stone-400">unnamed</span>}{o.dead ? <span className="ml-1 font-sans text-xs text-stone-400" data-testid="dead-note">(dead)</span> : null}</p>}
        asset={o.asset}
        line={`at ${squareWords(squareOf(o, store.state.grid, extent))} · yours — drag it, or step it with the arrow keys${o.locked ? ' · locked' : ''}`}
        lineTestId="thing-at"
        source={o.entry ? <p className="text-[0.71875rem] text-vtt-gold" data-testid="token-entry">{o.entry.name}</p> : undefined}
        owners={<p className="text-[0.71875rem] text-stone-400" data-testid="owners-line">{ownersLine(o, table.members, meId, store.state.objects)}</p>}
      ><Portrait name={o.name} asset={o.asset} /></IdentityHeader>
      {token && (
        <Section kind={kind} id="vitals" title="Vitals" summary={[bar ? `${bar.label} ${bar.current}/${bar.max}` : '', chips.length ? `${chips.length} condition${chips.length === 1 ? '' : 's'}` : '', o.dead ? 'dead' : ''].filter(Boolean).join(' · ')} open>
          {bar && <HpBar current={bar.current} max={bar.max} label={bar.label} />}
          <StatTiles tiles={tiles} />
          <ConditionChips chips={chips} />
          <ValuesEditor o={o} full={false} />
          <DeadToggle o={o} />
          <NoAooNote o={o} />
          <ResourcesEditor o={o} full={false} />
          <AstralControl o={o as { id: string; astral?: string }} />
          <JackControl o={o as { id: string; jacked?: true }} />
          <CyberPanel o={o as never} isDm={false} />
          <KnownFacts o={o} />
        </Section>
      )}
      {token && o.character && (
        <Section kind={kind} id="character" title="Character" summary="your character" open>
          <TokenCharacter o={o} />
        </Section>
      )}
      {token && systemRules.attack && (
        <Section kind={kind} id="actions" title="Actions" summary={actsAsLine(enc, o.id) ? 'in the fight' : ''} open={enc?.running === true}>
          <TokenActions o={o} />
          {actsAsLine(enc, o.id) && <p className="text-xs text-stone-400" data-testid="acts-as">{actsAsLine(enc, o.id)}</p>}
        </Section>
      )}
      {token && !systemRules.attack && actsAsLine(enc, o.id) && <p className="text-xs text-stone-400" data-testid="acts-as">{actsAsLine(enc, o.id)}</p>}
      {token && (
        <Section kind={kind} id="light" title="Light & vision" summary={lightVisionSummary(o, systemRules.vision, systemRules.distance)} open={false}>
          {/* Only the DM sets a token's light and senses (#1144, Tom's C): a co-DM with the tokens power, never an owner. */}
          {mayAct('object.light') && <LightControl o={o} />}
          {mayAct('object.vision') && <VisionControl o={o} />}
          <AurasControl o={o} />
        </Section>
      )}
      {token && (
        <Section kind={kind} id="token" title="Token" summary={o.follows ? 'follows another' : ''} open={false}>
          <RotateControl o={o} />
          <HeightControl o={o} />
          <FollowPicker o={o} />
        </Section>
      )}
      {token && o.shares && (
        <Section kind={kind} id="shared" title="Shared with others" summary={shared.length ? shared.join(', ') : 'nothing'} open={false}>
          <SharesEditor id={o.id} shares={o.shares ?? defaultShares(o.pc)} fields={shareFields} />
          <TellEditor id={o.id} told={o.told ?? []} fields={shareFields} />
        </Section>
      )}
    </section>
  );
}


/**
 * What the DM should know before revealing part of a numbered group
 * (design/22 Q2): the mates that stay hidden, since "Goblin 4" alone tells
 * the players 1 to 3 may exist. A reminder, never a stop.
 */
export function partialGroupNote(objects: DmObject[], ids: string[]): string {
  const revealing = new Set(ids);
  const names = objects.map((o) => o.name);
  const staying = new Set<string>();
  for (const o of objects) {
    if (!revealing.has(o.id)) continue;
    for (const mate of numberedMates(o.name, names)) {
      const other = objects.find((x) => x.name === mate);
      if (other && other.hidden && !revealing.has(other.id)) staying.add(mate);
    }
  }
  if (staying.size === 0) return '';
  const list = [...staying].sort();
  return `${list.length === 1 ? `${list[0]} stays` : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]} stay`} hidden — the numbering tells the players there may be more.`;
}

/**
 * Who an area catches (issue 1128), by the rule it was laid with, made here from the things this browser holds: the
 * DM's whole table, or the projection a player was sent — which has no hidden thing in it, so her list cannot name,
 * count or hint at one. Nothing is rolled; the DM reads it before asking for a save. Same-named creatures are named as
 * the turn order names them (*Goblin 2*, issue 1563), as the save rows that follow are.
 */
function AreaCaught({ area, extent }: { area: Area; extent: { width: number; height: number } }) {
  const { store } = useTableState();
  const caught = caughtBy(area, Object.values(store.state.objects) as Array<DmObject | PlayerObject>, store.state.grid, extent, (o) => elevation(o, store.state.scene));
  // An area laid by no rule for who it catches lists no one (issue 1305: hidden, not explained on every area).
  if (caught === null) return null;
  const text = caught.length === 0 ? 'Catches no one.'
      : `Catches ${caught.length}: ${caught.map((o) => `${tokenLabel(o, store.state.encounter) ?? 'an unnamed creature'}${'hidden' in o && o.hidden ? ' (hidden)' : ''}`).join(', ')}`;
  return <span className="basis-full text-stone-400" data-testid={`area-caught-${area.id}`}>{text}</span>;
}

/**
 * Vision (issue 1144, slice 2; design/30): the DM's switch for this scene — walls block what the players' characters
 * see, and a player is sent only what their sight reaches — and whose sight counts, the table's setting (§3.3).
 */
function VisionSetting() {
  const { store, act, systemRules } = useTableState();
  const vision = store.state.vision ?? { on: false, sight: 'party' as const };
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="vision">
      <label className="flex items-center gap-1" title="On, the players see only what their characters see past the walls, and the places they have explored. Off, walls only stop movement.">
        <input type="checkbox" aria-label="Vision: walls block sight" checked={vision.on} onChange={(e) => act({ kind: 'vision.set', on: e.target.checked })} /> Vision: walls block sight
      </label>
      <label className="flex items-center gap-1" title="The whole table's setting: what any party member sees, every player sees; or each player sees what their own character sees.">
        <span className="text-stone-400">Whose sight counts:</span>
        <select aria-label="Whose sight counts" className="rounded bg-stone-800 px-1" value={vision.sight} onChange={(e) => act({ kind: 'vision.set', sight: e.target.value === 'character' ? 'character' : 'party' })}>
          <option value="party">the party's, combined</option>
          <option value="character">each character's own</option>
        </select>
      </label>
      <label className="flex items-center gap-1" title="This scene's light (issue 1145): bright (daylight, every square lit), dim (dusk: every square lit, dimly) or dark (only lights light anything). It matters with vision on.">
        <span className="text-stone-400">Light:</span>
        <select aria-label="The scene's light" className="rounded bg-stone-800 px-1" value={store.state.lightLevel ?? 'bright'} onChange={(e) => act({ kind: 'light.level', level: e.target.value as LightLevel })}>
          <option value="bright">bright</option>
          <option value="dim">dim</option>
          <option value="dark">dark</option>
        </select>
      </label>
      <label className="flex items-center gap-1" title="A shortcut for the scene's light (issue 1163): day is bright, dawn or dusk dim, night dark. Nothing else changes with it.">
        <span className="text-stone-400">Time of day:</span>
        <select aria-label="Time of day" className="rounded bg-stone-800 px-1" value={timeOfDay(store.state.lightLevel)} onChange={(e) => act({ kind: 'light.level', level: TIMES_OF_DAY.find((t) => t.key === e.target.value)?.level ?? 'bright' })}>
          {TIMES_OF_DAY.map((t) => <option key={t.key} value={t.key}>{t.label.toLowerCase()}</option>)}
        </select>
      </label>
      {/* Weather (issue 1163): the game system's conditions; hidden when it declares none (issue 1305's rule). */}
      {systemRules.weather.length > 0 && (
        <label className="flex items-center gap-1" title={`This scene's weather. With vision on, a condition the rules give a sight limit cuts every character's sight to it, and the players see its name.${systemRules.weather.map((w) => (w.notes ? ` ${w.label}: ${w.notes}.` : '')).join('')}`}>
          <span className="text-stone-400">Weather:</span>
          <select aria-label="Weather" className="rounded bg-stone-800 px-1" value={store.state.weather?.key ?? ''} onChange={(e) => act({ kind: 'weather.set', key: e.target.value || null })}>
            <option value="">none</option>
            {systemRules.weather.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

/**
 * The things on this scene (issue 1575): a list the DM can search and group by layer, a hidden one marked 🔒; a
 * player's own tokens. Each selects its token; the DM's rows reveal, hide and remove; anyone steps what they may move.
 */
function ObjectsList({ listed, ownOnly, matched, extent, selected, step, canMove, reveal, hide, searchable }: {
  listed: Thing[]; ownOnly: boolean; matched?: Matched | null; extent: { width: number; height: number }; selected: ReadonlySet<string>;
  step: (o: Thing, di: number, dj: number) => void; canMove: (o: Thing) => boolean; reveal: (ids: string[]) => void; hide: (ids: string[]) => void; searchable: boolean;
}) {
  const { store, systemRules } = useTableState();
  const words = useNumberWords();
  const mayAct = useMayAct();
  const [q, setQ] = useState('');
  const [byLayer, setByLayer] = useState(false);
  const text = q.trim().toLowerCase();
  const shown = text ? listed.filter((o) => (o.name ?? '').toLowerCase().includes(text)) : listed;
  const row = (o: Thing) => {
    const hidden = 'hidden' in o && o.hidden;
    const line = valuesLine(o.values, words, systemRules.resources, systemRules.health);
    return (
      <li key={o.id} data-testid={`object-${o.id}`} data-name={o.name} className={`py-2 flex flex-wrap items-center gap-2 ${hidden ? 'opacity-50' : ''} ${selected.has(o.id) ? 'bg-stone-800/60 rounded px-1' : ''}`}>
        {/* A creature whose name the DM withholds (COMBAT-007) is still a thing to select: "unnamed", by where it stands. */}
        <button type="button" className="font-medium text-left hover:underline" aria-pressed={selected.has(o.id)} aria-label={`Select ${o.name ?? `the unnamed token at ${squareWords(squareOf(o, store.state.grid, extent))}`}`} onClick={() => {
          selection.only(o.id);
          // The keys go to the board (the 2026-10-15 session pass): the panel says "step it with the arrow keys",
          // and they did nothing until the board itself was clicked — this button leaves the list as the token is
          // chosen, and the focus fell to the page.
          document.querySelector<HTMLElement>('[data-testid="board-host"]')?.focus();
        }}>
          {hidden && <span aria-hidden="true">🔒 </span>}{o.name ?? <span className="italic text-stone-400">unnamed</span>}
        </button>
        {matched?.id === o.id && <MatchSwatch colour={matched.colour} of={o.name ?? 'This token'} />}
        <span className="text-stone-500">{squareWords(squareOf(o, store.state.grid, extent))}{hidden ? ' · hidden' : ''}{'mine' in o && o.mine ? ' · yours' : ''}{o.locked ? ' · locked' : ''}{o.asset ? ' · pictured' : ''}{line ? ` · ${line}` : ''}</span>
        {canMove(o) && !o.locked && (
          <span className="flex gap-1">
            <button type="button" className={btn} aria-label={`Move ${o.name} left`} onClick={() => step(o, -1, 0)}>←</button>
            <button type="button" className={btn} aria-label={`Move ${o.name} up`} onClick={() => step(o, 0, -1)}>↑</button>
            <button type="button" className={btn} aria-label={`Move ${o.name} down`} onClick={() => step(o, 0, 1)}>↓</button>
            <button type="button" className={btn} aria-label={`Move ${o.name} right`} onClick={() => step(o, 1, 0)}>→</button>
          </span>
        )}
        {(mayAct('object.reveal') || mayAct('object.remove')) && (
          <span className="ml-auto flex gap-1">
            {mayAct('object.reveal') && 'hidden' in o && <button type="button" className={btn} aria-label={`${hidden ? 'Reveal' : 'Hide'} ${o.name}`} onClick={() => (hidden ? reveal([o.id]) : hide([o.id]))}>
              {hidden ? 'Reveal' : 'Hide'}
            </button>}
            {mayAct('object.remove') && <button type="button" className={btn} aria-label={`Remove ${o.name}`} onClick={() => boardBus.requestDelete([o.id])}>Remove</button>}
          </span>
        )}
      </li>
    );
  };
  const layers: Array<[string, string]> = [['token', 'Tokens'], ['scenery', 'Scenery'], ['dm', 'DM layer']];
  const layerOf = (o: Thing) => ('layer' in o ? (o as DmObject).layer : 'token');
  return (
    <>
      {searchable && listed.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <input type="search" aria-label="Find on this scene" placeholder="Find by name…" className="min-w-0 flex-1 rounded border border-stone-700 bg-stone-900 px-1.5 py-0.5" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="flex items-center gap-1"><input type="checkbox" aria-label="Group by layer" checked={byLayer} onChange={(e) => setByLayer(e.target.checked)} /> by layer</label>
        </div>
      )}
      <ul className="divide-y divide-stone-800" data-testid="objects">
        {/* A first-time DM met "Nothing on the table yet." and nothing about how to fill it: the two ways in are said. */}
        {!ownOnly && store.state.objects && Object.keys(store.state.objects).length === 0 && (
          <li className="py-2 text-stone-500" data-testid="no-objects">
            Nothing on the table yet.
            {mayAct('object.add') && ' Add a marker below, or upload a picture in the Library (Table menu) to place it as a token or use it as the map.'}
          </li>
        )}
        {text && shown.length === 0 && listed.length > 0 && <li className="py-2 text-stone-500" data-testid="no-match">Nothing here is called that.</li>}
        {byLayer
          ? layers.map(([key, label]) => {
            const mine = shown.filter((o) => layerOf(o) === key);
            return mine.length ? (
              <li key={`layer-${key}`} className="py-1" data-testid={`layer-${key}`}>
                <p className="text-[0.65625rem] font-semibold uppercase tracking-[0.08em] text-vtt-gold-dim">{label}</p>
                <ul className="divide-y divide-stone-800">{mine.map(row)}</ul>
              </li>
            ) : null;
          })
          : shown.map(row)}
      </ul>
    </>
  );
}

/** The areas and focuses on the table (issues 832, 1128; slice 13): the table's, shown whatever is selected. */
function AreasAndFocuses({ extent }: { extent: { width: number; height: number } }) {
  const { table } = useShell();
  const { store, act, meId, systemRules } = useTableState();
  const mayAct = useMayAct();
  return (
    <>
      {Object.keys(store.state.areas ?? {}).length > 0 && (
        <div data-testid="areas">
          <h3 className="vtt-section-head">Areas</h3>
          <ul className="divide-y divide-stone-800">
            {Object.values(store.state.areas ?? {}).map((a) => {
              const owner = table.members.find((m) => m.id === a.ownerId);
              const who = memberLabel(owner, store.state.objects, { meId, you: 'you' });
              const p = intersectionAt(store.state.grid, extent, a.origin);
              const what = areaSizeLabel(a, systemRules.distance);
              return (
                <li key={a.id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs" data-testid={`area-${a.id}`}>
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: owner?.colour ?? '#a8a29e' }} aria-hidden="true" />
                  <span className="text-stone-300">{who}</span>
                  <span className="text-stone-400">{a.label || what}</span>
                  {a.label && <span className="text-stone-500">{what}</span>}
                  <span className="ml-auto flex gap-1">
                    <button type="button" className={btn} aria-label={`Go to ${a.label || what}`} onClick={() => boardBus.goTo(p)}>Go to</button>
                    {mayClearArea(a, { id: meId, role: mayAct('area.clear') ? 'dm' : 'player' }) && <button type="button" className={btn} aria-label={`Clear ${a.label || what}`} onClick={() => act({ kind: 'area.clear', id: a.id })}>Clear</button>}
                  </span>
                  <ResolveSaves area={a} label={a.label || what} extent={extent} />
                  <AreaCaught area={a} extent={extent} />
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {Object.keys(store.state.focuses ?? {}).length > 0 && (
        <div data-testid="focuses">
          <h3 className="vtt-section-head">Focuses</h3>
          <ul className="divide-y divide-stone-800">
            {Object.values(store.state.focuses).map((f) => {
              const owner = table.members.find((m) => m.id === f.ownerId);
              const who = memberLabel(owner, store.state.objects, { meId, you: 'you' });
              const r = focusRect(f.cells, store.state.grid, extent);
              return (
                <li key={f.id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs" data-testid={`focus-${f.id}`}>
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: owner?.colour ?? '#a8a29e' }} aria-hidden="true" />
                  <span className="text-stone-300">{who}</span>
                  <span className="text-stone-400">{f.note || '(no note)'}</span>
                  <span className="text-stone-500">{f.cells.length === 1 ? '1 square' : `${f.cells.length} squares`}</span>
                  <span className="ml-auto flex gap-1">
                    <button type="button" className={btn} aria-label={`Go to ${who === 'you' ? 'your' : `${who}'s`} focus`} onClick={() => boardBus.goTo({ x: r.x + r.width / 2, y: r.y + r.height / 2 })}>Go to</button>
                    {mayClearFocus(f, { id: meId, role: mayAct('focus.clear') ? 'dm' : 'player' }) && <button type="button" className={btn} aria-label={`Clear ${who === 'you' ? 'your' : `${who}'s`} focus`} onClick={() => act({ kind: 'focus.clear', id: f.id })}>Clear</button>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

/** The scene's sections (issue 1575), the DM's with nothing selected: its map and grid, sight and light, its rules, what tokens show. */
function SceneView({ extent }: { extent: { width: number; height: number } }) {
  const { table, settings, onSettings } = useShell();
  const boardMap = useBoardMap(); // the scene on this screen (issue 834)
  const { store, act, systemRules, scene, rulings } = useTableState();
  const words = useNumberWords();
  const mayAct = useMayAct();
  const [choosing, setChoosing] = useState(false);
  const objects = Object.values(store.state.objects) as Thing[];
  const hiddenCount = objects.filter((o) => 'hidden' in o && o.hidden).length;
  const markers = objects.filter((o) => !o.asset).length;
  const name = scene ? table.scenes?.find((x) => x.id === scene.id)?.name ?? null : null;
  const grid = store.state.grid;
  const vision = store.state.vision ?? { on: false, sight: 'party' as const };
  const overlays = store.state.scene.overlays;
  const onTokens = OVERLAY_KEYS.filter((k) => overlays[k]).map((k) => overlayShort(words)[k]);
  return (
    <section data-testid="scene" className="space-y-2">
      <IdentityHeader
        name={<h3 className="font-display text-base font-semibold text-vtt-fg" data-testid="scene-name">{name ?? 'This scene'}</h3>}
        asset={null}
        line={`${boardMap ? `${boardMap.width} × ${boardMap.height}` : 'the empty grid'} · ${grid.mode === 'none' ? 'no grid' : `squares of ${Math.round(grid.x.basePitch)} px`}`}
        owners={<p className="text-[0.71875rem] text-stone-400" data-testid="scene-counts">{objects.length} {objects.length === 1 ? 'thing' : 'things'} · {hiddenCount} hidden · {markers} {markers === 1 ? 'marker' : 'markers'}</p>}
      ><span className="grid h-[3.25rem] w-[3.25rem] shrink-0 place-items-center rounded-full border-2 border-vtt-gold bg-vtt-raised text-xl" aria-hidden="true">▦</span></IdentityHeader>
      <Section kind="scene" id="map" title="Map & grid" summary={boardMap ? boardMap.name : 'the empty grid'} open>
        <p>
          Map: <span data-testid="scene-map">{boardMap ? `${boardMap.name} (${boardMap.width} × ${boardMap.height})` : 'none — the empty grid'}</span>
        </p>
        {mayAct('scene.set') && !choosing && <button type="button" className={btn} onClick={() => setChoosing(true)}>Change map…</button>}
        {mayAct('scene.set') && choosing && <MapChooser onClose={() => setChoosing(false)} />}
        {mayAct('grid.set') && <GridSetup />}
        {mayAct('scene.set') && <label className="flex items-center gap-1 text-xs" title="On, a moved token sits at a cell centre; off, placement is free. Turning it on moves nothing already placed.">
          <input type="checkbox" aria-label="Snap to the grid" checked={store.state.scene.snap} onChange={(e) => act({ kind: 'scene.set', scene: { ...store.state.scene, snap: e.target.checked } })} /> Snap to the grid
        </label>}
        {mayAct('scene.set') && <label className="flex items-center gap-1 text-xs" title="On, no reachable area and no confirmation: tokens drag freely in this scene. Players are told when it changes.">
          <input type="checkbox" aria-label="Free move" checked={store.state.scene.freeMove === true} onChange={(e) => { const { freeMove: _f, ...rest } = store.state.scene; act({ kind: 'scene.set', scene: { ...rest, ...(e.target.checked ? { freeMove: true as const } : {}) } }); }} /> Free move
        </label>}
      </Section>
      {mayAct('vision.set') && (
        <Section kind="scene" id="vision" title="Vision & light" summary={`${vision.on ? 'walls block sight' : 'vision off'} · ${store.state.lightLevel ?? 'bright'}${store.state.weather ? ` · ${store.state.weather.label ?? store.state.weather.key}` : ''}`} open={false}>
          <VisionSetting />
        </Section>
      )}
      <Section kind="scene" id="combat" title="Combat rules" summary={systemRules.borrowed ? `borrowed from ${systemRules.borrowed}` : 'its own system\'s'} open={false}>
        <CombatRulesSetting />
        <OverrideSetting />
      </Section>
      {/* The DM's rulings (issue 1136), beside the table's other rules setting. */}
      <Section kind="scene" id="rulings" title="Rulings" summary={rulings.length ? `${rulings.length} in force` : 'none'} open={false}>
        <RulingsSection />
      </Section>
      {mayAct('scene.set') && (
        <Section kind="scene" id="overlays" title="On tokens" summary={onTokens.length ? onTokens.join(', ') : 'nothing'} open={false}>
          <span className="flex flex-wrap items-center gap-2 text-xs" data-testid="overlays" title="Which overlays this table draws on its tokens. A table that never tracks a slot never renders its chip on anything.">
            {OVERLAY_KEYS.map((k) => (
              <label key={k} className="flex items-center gap-1" title={k === 'hp' ? `Drawn for you on every creature; a player sees a token's ${words.health} bar only where its ${words.health} is shared with them, token by token (Players see).` : undefined}>
                <input type="checkbox" aria-label={`Draw ${overlayLabels(words)[k]}`} checked={overlays[k]} onChange={(e) => act({ kind: 'scene.set', scene: { ...store.state.scene, overlays: { ...overlays, [k]: e.target.checked } } })} /> {k === 'hp' ? `${words.health} bars: DM only` : overlayLabels(words)[k]}
              </label>
            ))}
          </span>
        </Section>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-1">
          <input type="checkbox" aria-label="Ask before deleting" checked={settings.confirmDelete} onChange={async (e) => onSettings(await saveSettings({ confirmDelete: e.target.checked }))} /> Ask before deleting
        </label>
        {mayAct('object.restore') && store.state.undelete && store.state.undelete.length > 0 && (
          <button type="button" className={btn} data-testid="undelete" onClick={() => act({ kind: 'object.restore' })}>
            Undelete {store.state.undelete.length === 1 ? store.state.undelete[0]!.name : `${store.state.undelete.length} things`}
          </button>
        )}
      </div>
    </section>
  );
}

/** The overlays' short words, for the On tokens summary. */
const overlayShort = (w: NumberWords): Record<keyof Overlays, string> => ({ hp: `${w.health} bars`, ac: w.defence, slot1: 'slot 1', slot2: 'slot 2', conditions: 'conditions' });

/** What ability damage and drain took from this token, for its DM and its owners: the DM clears it as the creature recovers. */
function AbilityLossRow({ id, loss }: { id: string; loss: NonNullable<Values['abilityLoss']> }) {
  const { act } = useTableState();
  const mayAct = useMayAct();
  const words = Object.entries(loss).map(([k, l]) => `${k.toUpperCase()} ${[l.damage ? `${l.damage} damage` : '', l.drain ? `${l.drain} drain` : ''].filter(Boolean).join(', ')}`);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-stone-300" data-testid="ability-loss">
      <span>Lost: {words.join('; ')}</span>
      {mayAct('table.rules') && <button type="button" className="rounded bg-stone-700 px-2 py-0.5 hover:bg-stone-600" onClick={() => act({ kind: 'object.values', id, values: { abilityLoss: null } } as never)}>Recovered</button>}
    </div>
  );
}

export function PropertiesPanel() {
  const { table } = useShell();
  const boardMap = useBoardMap(); // the scene on this screen (issue 834)
  const { store, role, act, setNotice } = useTableState();
  const selected = useSelection();
  const [newName, setNewName] = useState('');
  const [newHidden, setNewHidden] = useState(true);
  // Each control by the act it sends (issue 1160): a co-DM gets what their powers hold, whichever board they see; the
  // editors that read the DM's own fields (`hidden`, `rev`) need that board too (`'hidden' in o`).
  const mayAct = useMayAct();
  const movesAny = mayAct('object.move');
  const sceneControls = mayAct('scene.set') || mayAct('table.rules') || mayAct('ruling.add') || mayAct('object.restore');
  const objects = Object.values(store.state.objects) as Thing[];
  const extent = boardExtent(boardMap);
  const encounter = store.state.encounter as Encounter | PlayerEncounter | undefined;
  const canMove = (o: Thing) => movesAny || ('mine' in o && o.mine);
  // During a fight a step from the panel is refused on the screen first, as a drag is (design/20 §4.1).
  const step = (o: Thing, di: number, dj: number) => {
    const refusal = !movesAny && encounter && 'active' in encounter ? previewRefusal(encounter, o.id, 'player') : null;
    if (refusal) { setNotice(refusal); return; }
    const to = stepCell(store.state.grid, extent, { x: o.x, y: o.y }, o.size, di, dj);
    // Placing the table (issue 811): the step lands with no route, as a drag does.
    act({ kind: 'object.move', id: o.id, route: movesAny && role === 'dm' && store.state.placing ? [to] : [{ x: o.x, y: o.y }, to], rev: moveRev(o) });
  };
  // A player's list (issue 1347, Tom's C): only their own character's token(s), a shortcut to their own sheet and
  // controls; the Tokens tool lists everything they are shown. The DM, a co-DM and a watcher keep the whole list. Under
  // View as, `role` and the store are the viewed person's, so this follows them. A co-DM shown a player's board (`role`
  // 'player') keeps the whole list.
  const ownOnly = role === 'player' && table.me.role !== 'codm';
  const picked = objects.filter((o) => selected.has(o.id));
  const one = picked.length === 1 ? picked[0]! : null;
  // A player's own tokens, less the one already shown whole above (it was listed twice).
  const listed = ownOnly ? objects.filter((o) => o.kind === 'token' && 'mine' in o && o.mine && o.id !== one?.id) : objects;
  // One event for several: an ambush lands together (design/21 §3); the board's gate acts and carries the reminder.
  const reveal = (ids: string[]) => boardBus.requestReveal(ids, true);
  const hide = (ids: string[]) => boardBus.requestReveal(ids, false);
  const areaCount = Object.keys(store.state.areas ?? {}).length;
  const focusCount = Object.keys(store.state.focuses ?? {}).length;
  const matched = useColourMatch(selected);
  const tint = matched && one?.id === matched.id ? matched.colour : undefined;

  return (
    <div className={`space-y-2 p-2 text-sm ${tint ? 'rounded border-2' : ''}`} data-testid="properties" data-tint={tint} style={tint ? { borderColor: tint } : undefined}>
      {one && 'values' in one && one.values.abilityLoss && <AbilityLossRow id={one.id} loss={one.values.abilityLoss} />}
      {/* The DM's rulings in force (issue 1136), as a player reads them: read-only, whatever is selected. */}
      {!sceneControls && <RulingsSection />}
      {sceneControls && !one && <SceneView extent={extent} />}

      {mayAct('object.rename') && one && 'hidden' in one && <ThingEditor o={one} reveal={reveal} extent={extent} tint={tint} />}
      {mayAct('object.reveal') && picked.length > 1 && (
        <section className="flex flex-wrap items-center gap-2" data-testid="many-selected">
          <span>{picked.length} selected</span>
          <button type="button" className={btn} onClick={() => reveal(picked.map((o) => o.id))}>Reveal all</button>
          <button type="button" className={btn} onClick={() => hide(picked.map((o) => o.id))}>Hide all</button>
          {(() => { const free = picked.filter((o) => o.kind === 'token' && !combatantOf((store.state.encounter ?? emptyEncounter()) as Encounter, o.id)) as DmObject[]; return free.length > 0 && <button type="button" className={btn} aria-label={`Add ${free.length} to the encounter`} onClick={() => addToEncounter(act, free)}>Add {free.length} to encounter</button>; })()}
          <button type="button" className={btn} aria-label={`Remove ${picked.length} things`} onClick={() => boardBus.requestDelete(picked.map((o) => o.id))}>Delete {picked.length}</button>
          <button type="button" className={btn} onClick={() => selection.clear()}>Clear selection</button>
        </section>
      )}
      {one && !(mayAct('object.rename') && 'hidden' in one) && (
        'mine' in one
          ? (one.mine ? <OwnThing o={one} extent={extent} /> : <UnknownThing o={one} extent={extent} />)
          // The DM's board without the power to edit it (a co-DM): what it is and whose, as before.
          : 'hidden' in one ? <DmBoardView o={one} extent={extent} /> : <UnknownThing o={one as PlayerObject} extent={extent} />
      )}
      {ownOnly && !one && listed.length === 0 && <p className="text-stone-400" data-testid="no-own-tokens">Nothing selected. Your character's token appears here once it is on the board.</p>}

      {/* Area templates (issue 832) and focuses: the table's, shown whatever is selected. */}
      {(areaCount > 0 || focusCount > 0) && (
        <Section kind="scene" id="areas" title="Areas & focuses" summary={[areaCount ? `${areaCount} area${areaCount === 1 ? '' : 's'}` : '', focusCount ? `${focusCount} focus${focusCount === 1 ? '' : 'es'}` : ''].filter(Boolean).join(' · ')} open={!sceneControls}>
          <AreasAndFocuses extent={extent} />
        </Section>
      )}

      {ownOnly
        ? <ObjectsList listed={listed} ownOnly matched={matched} extent={extent} selected={selected} step={step} canMove={canMove} reveal={reveal} hide={hide} searchable={false} />
        : (
          <Section kind="scene" id="objects" title="Objects on this scene" summary={`${objects.length} ${objects.length === 1 ? 'thing' : 'things'}`} open>
            <ObjectsList listed={listed} ownOnly={false} matched={matched} extent={extent} selected={selected} step={step} canMove={canMove} reveal={reveal} hide={hide} searchable={!!sceneControls} />
          </Section>
        )}

      {mayAct('object.add') && !one && (
        <form className="flex flex-wrap items-center gap-2" data-testid="add-marker" onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          const at = snapToCells(store.state.grid, extent, { x: EMPTY_GRID.squarePx * 1.5, y: EMPTY_GRID.squarePx * 1.5 }, { w: 1, h: 1 }).centre;
          act({ kind: 'object.add', object: { id: crypto.randomUUID(), kind: 'token', name: newName.trim(), x: at.x, y: at.y, size: { w: 1, h: 1 }, fit: 'stretch', layer: 'token', hidden: newHidden, owners: [], asset: null, locked: false, pc: false, values: {}, shares: defaultShares(false), nameplate: true } });
          setNewName('');
        }}>
          <label htmlFor="new-marker">+ Marker</label>
          <input id="new-marker" aria-label="Add a marker" className="w-32 rounded bg-stone-800 px-2 py-1" value={newName} maxLength={120} onChange={(e) => setNewName(e.target.value)} />
          <label className="flex items-center gap-1"><input type="checkbox" checked={newHidden} onChange={(e) => setNewHidden(e.target.checked)} /> hidden</label>
          <button type="submit" className="rounded border border-stone-600 px-2 py-1">Add</button>
        </form>
      )}
    </div>
  );
}

/** A thing on the DM's board for someone who may not edit it (a co-DM without the tokens power): what and whose. */
function DmBoardView({ o, extent }: { o: DmObject; extent: { width: number; height: number } }) {
  const { store, meId } = useTableState();
  const { table } = useShell();
  return (
    <section className="space-y-2" data-testid="thing-view">
      <IdentityHeader name={<p className="font-display text-base font-semibold text-vtt-fg">{o.name}{o.dead ? <span className="ml-1 font-sans text-xs text-stone-400" data-testid="dead-note">(dead)</span> : null}</p>}
        asset={o.asset} line={`at ${squareWords(squareOf(o, store.state.grid, extent))}${o.locked ? ' · locked' : ''}`} lineTestId="thing-at"
        owners={<p className="text-[0.71875rem] text-stone-400" data-testid="owners-line">{ownersLine(o, table.members, meId, store.state.objects)}</p>}
      ><Portrait name={o.name} asset={o.asset} /></IdentityHeader>
      <TokenCharacter o={o} />
      <TokenEntry o={o} />
      <TokenActions o={o} />
      {o.kind === 'token' && actsAsLine(store.state.encounter as Encounter | undefined, o.id) && <p className="text-xs text-stone-400" data-testid="acts-as">{actsAsLine(store.state.encounter as Encounter | undefined, o.id)}</p>}
    </section>
  );
}
